import { captureLinkFromAddress, pendingLink, watchLinkGone, openPendingLinkForPage } from "./ui/link-join.js";
import { openSessionResume } from "./ui/session-resume.js";
import { createTaskFilter, setTaskFilter } from './ui/note-task-filter.js';
import { createTagFilter } from '../../engine/core/tag-filter.js';
import { inProgressTags } from '../../engine/core/node-tags.js';
import * as RoutinesView from './ui/routines.js';
import * as MapSearch from './ui/search.js';
import { initQuotaNotice } from "./ui/quota-notice.js";
import { initMapSizeNotice } from "./ui/map-size-notice.js";
/**
 * Main Application Bootstrap
 * Initializes the modular architecture and wires everything together.
 * Works both offline (localStorage) and online (backend with E2EE).
 */

import { initSyncPausedNotice } from './ui/sync-paused.js';
// Import module system (exposes window.Orbital* globals)
import "./modules.js";
import { buildHorizonPredicate, isHorizonActive, HORIZON_VIEWS } from "../../engine/core/horizon.js";

// Import Store and Views
import * as Store from "./store/index.js";
import { initTaskPostponement } from "./ui/task-postponement.js";
import * as NotesOverlay from "./views/notes/overlay-lazy.js";
import * as KanbanView from "./views/kanban/index.js";
import * as DashboardView from "./views/dashboard/index.js";
import * as GanttView from "./views/gantt/index.js";
import * as MindmapView from "./views/mindmap/index.js";
import * as MindmapState from "./views/mindmap/state.js";
import * as MindmapZoomPan from "./views/mindmap/zoom-pan.js";
import * as BubbleView from "./views/bubble/index.js";
import * as BubbleState from "./views/bubble/state.js";

// Import local document state (per-doc settings that don't sync)
import {
  getDocState,
  saveDocState,
  getLastOpenedDoc,
  setLastOpenedDoc,
  clearLastOpenedDoc,
} from "../../shared/local-doc-state.js";
import { setBubblePosition } from "./store/bubble-positions.js";
import {
  getPref,
  setPref,
  isSyncAvailable,
  getVaultPath,
  setVaultPath,
  reloadApp,
  getServerOrigin,
  setServerUrl,
  loadAdapterConfig,
  installFetchProxy,
} from "../../engine/storage/index.js";

// Import Vault storage
import {
  VaultStorage,
  initVaultStorage,
  enableUserCRDT,
  disableUserCRDT,
} from "../../engine/crypto/vault-storage.js";
import * as VaultUI from "./ui/vault/index.js";
import { getUITheme, setUITheme, initThemes } from "./ui/theme.js";

// Import Auth handlers
import * as AuthHandlers from "../../engine/auth/handlers.js";
import {
  getOrCreateLocalUser,
  getOrCreateLocalMasterKey,
  getCachedUserId,
  getCachedUsername
} from "../../engine/auth/handlers.js";

// Import App state management
import { setCurrentUser, getCurrentUser } from "../../engine/account.js";
import { initI18n, t } from "../../shared/i18n/index.js";
import { mountLanguageButtons } from "./ui/language-button.js";

// Import CRDT Sync
import * as CRDTBridge from "../../engine/sync/crdt-bridge.js";
import { generateNodeId as generateId } from "../../engine/sync/crdt-bridge.js"; // Phase 5.1: Unified ID generation
import * as UserCRDTBridge from "../../engine/sync/user-crdt-bridge.js";
import * as AccessRequests from "../../engine/sync/access-requests.js";
import { ConnectionState } from "../../engine/sync/websocket.js";
import { setGetCurrentUser } from "./ui/update-manager.js";
import { setAttachmentDekResolver, setAttachmentDekLoader, setAttachmentStorageResolver, getAttachmentDek } from "../../engine/services/attachment-service.js";
import { on, emit, Events } from "../../engine/core/events.js";

// Wire the attachment-service DEK lookup so the image NodeView can resolve
// `attachment:DOC/BLOB` srcs to displayable object URLs without a circular
// import (attachment-service ↔ crdt-bridge via share-revocation).
setAttachmentDekResolver((docId) => CRDTBridge.getEncryptionKeyForDoc(docId));
// A map on this device keeps its attachments on the device.
setAttachmentStorageResolver((docId) => MapStorage.mapStorageOf(docId));

// Async fallback for the cross-doc paste relinker: when the source doc isn't
// currently loaded (most common case — user pastes from doc A into doc B
// while only B is open), unwrap the DEK from the vault. Owned docs only;
// shared docs without an active sync manager fall through to null and the
// relinker leaves the ref alone.
setAttachmentDekLoader(async (docId, { create = false } = {}) => {
  try {
    // A map on this device has its own key (created at its first attachment).
    const dekBytes = await VaultStorage.getAttachmentKeyBytes(docId, { create });
    if (!dekBytes) return null;
    return await crypto.subtle.importKey(
      "raw",
      dekBytes,
      { name: "AES-GCM" },
      false,
      ["encrypt", "decrypt"],
    );
  } catch {
    return null;
  }
});

// Inject getCurrentUser into update-manager to break circular dependency
// (update-manager.js -> app.js -> vault.js -> crdt-bridge.js -> websocket.js -> update-manager.js)
setGetCurrentUser(getCurrentUser);

// Import Share and Invitations modules
import {
  fetchShareDetails,
  inviteUser as shareInviteUser,
  updateUserRole as shareUpdateUserRole,
  removeUser as shareRemoveUser,
  cancelInvitation as shareCancelInvitation,
  unshare as shareUnshare,
  linkActions as shareLink,
  initShareModule,
  destroyShareModule,
  keypairManager,
} from "./ui/share-modal.js";
import {
  initInvitations,
  destroyInvitations,
  showInvitationsModal,
  getInvitations,
  acceptInvitation,
  declineInvitation,
  ignoreInviter,
  getPendingNotifications,
  recoverRevokedShare,
  markNotificationRead,
} from "./ui/notifications.js";
import { initToast, showToast } from "./ui/toast.js";
import { initStorageBrokenModal } from "./ui/storage-broken-modal.js";
import { initUndoRedoToolbar } from "./ui/undo-redo-toolbar.js";
import {
  showModal as showModalGeneric,
  closeModal as closeModalGeneric,
  isModalOpen,
  initModals,
  confirm as confirmModal,
} from "./ui/modals.js";
import * as ContextMenu from "./ui/context-menu.js";
import { Keypair } from "../../engine/crypto/keypair.js";
import { showDeletionModal } from "./ui/account-deletion.js";
import { importAllData } from "../../engine/crypto/data-export.js";

// Import inline editor utilities
import {
  setupInlineEditor,
  makeEditable,
  escapeHtml,
  getThemeApi,
  getCurrentPalette,
  getNewColor,
} from "./ui/inline-editor.js";


// Import managers
import * as KeyboardManager from "./managers/keyboard-manager.js";
import * as ImportExportManager from "./managers/import-export.js";
import { cleanNode } from "./managers/import-export.js";
import { transferClipboardTags } from "../../engine/core/clipboard-tags.js";
import * as ContextMenuManager from "./managers/context-menu-manager.js";
import { initShortcutHelp, showShortcutHelp } from "./ui/shortcut-help.js";
import * as ModalManager from "./managers/modal-manager.js";
import * as DocumentManager from "../../engine/sync/document-manager.js";
import * as AuthManager from "./managers/auth-manager.js";
import { updateHeaderUI } from "./managers/auth-manager.js";
import { showScreen, getCurrentScreen } from "./ui/auth/screens.js";
import * as AuthForms from "./ui/auth/forms.js";
import { api } from "../../engine/auth/handlers.js";
import * as DocumentLoader from "./managers/document-loader.js";
import { closeMap } from "../../engine/open-map.js";
import * as Interfaces from "../index.js";
import { isClassicShown } from "../index.js";
import { installEngineHost } from "./ui/engine-host.js";
import { engine } from "../../engine/index.js";
import * as ProfileManager from "./managers/profile-manager.js";
import * as ShareManager from "../../engine/sync/share-manager.js";
import * as DocPropertiesModal from "./ui/doc-properties-modal.js";
import * as MapStorage from "./ui/map-storage.js";
import * as OfflineFiles from "../../engine/services/offline-files.js";
import { deleteWaitingFiles } from "../../engine/services/waiting-attachments.js";
import * as SyncOrchestrator from "./managers/sync-orchestrator.js";
import { initRefusedChanges } from "../../engine/sync/refused-changes.js";
import { mayHaveOtherMembers } from "../../engine/services/map-members.js";
import { canOpenLinkedMap } from "../../engine/sync/link-state.js";
import { scheduleVaultRepair } from "../../engine/sync/vault-repair.js";
import * as ViewOrchestrator from "./managers/view-orchestrator.js";
import * as OfflineMode from "./managers/offline-mode.js";
import * as SidebarManager from "./managers/sidebar-manager.js";
import * as SidebarTags from "./ui/sidebar-tags.js";
import * as TagPopup from "./ui/tag-popup.js";
import * as MultiSelect from "./ui/multi-select.js";
import * as MobileApp from "./mobile/index.js";
import * as LoadProgress from "./ui/load-progress.js";
import { isMobileShellVisible, MOBILE_SHELL_MEDIA_QUERY } from "../../shared/utils/device-detect.js";
import { APP_VERSION } from "../../engine/sync/version.js";

// ============================================
// Constants
// ============================================

const ROOT_COLOR = "#374151";

// Phase 5.1: generateId moved to crdt-bridge.js (generateNodeId) for unified ID generation
// Imported above as: import { generateNodeId as generateId } from "../../engine/sync/crdt-bridge.js";

// Inline editor functions (makeEditable, escapeHtml, getThemeApi, getCurrentPalette, getNewColor)
// moved to interfaces/classic/ui/inline-editor.js

// ============================================
// Shared Callbacks for Views
// ============================================

const sharedCallbacks = {
  // Store access (for views that need to read state)
  getState: () => Store.getState(),
  find: (id) => Store.find(id),

  // Tag definitions and filtering
  getTagDefinitions: (nodeId) => CRDTBridge.getTagDefinitions(nodeId),
  getTagCategories: () => CRDTBridge.getTagCategories(),
  // One tag filter per render pass (visibility + highlight rule, engine/core/tag-filter.js).
  makeTagFilter: () => createTagFilter({
    definitions: CRDTBridge.getTagDefinitions(),
    categories: CRDTBridge.getTagCategories(),
    definitionsFor: (node) => CRDTBridge.getTagDefinitions(node.id),
  }),
  getHighlightedTagIds: () => Store.get('highlightedTagIds'),
  getHiddenTagIds: () => Store.get('hiddenTagIds'),
  nodeHasTag: (node, tagId) => CRDTBridge.nodeHasTag(node, tagId),
  nodeIsDone: (node) => CRDTBridge.nodeIsDone(node),
  nodeIsInProgress: (node) => CRDTBridge.nodeIsInProgress(node),
  getDoneTagId: (nodeId) => CRDTBridge.getDoneTagId(nodeId),
  getEffectiveStatus: (node) => CRDTBridge.getEffectiveStatus(node),
  getTagId: (tagEntry) => CRDTBridge.getTagId(tagEntry),
  toggleNodeTag: (nodeId, tagId) => CRDTBridge.toggleNodeTag(nodeId, tagId),

  // Execution model — Today lists (engine/sync/execution-map.js, execution-stats.js)
  isExecutionEnabled: () => CRDTBridge.isExecutionEnabled(),
  getExecutionItems: (opts) => CRDTBridge.getExecutionItems(opts),
  canPostponeTask: CRDTBridge.canPostponeTask,
  getTaskPostponement: CRDTBridge.getTaskPostponement,
  sendToExecution: (noteId, nodeId, opts) => CRDTBridge.sendToExecution(noteId, nodeId, opts),
  markNodeInProgress: (nodeId) => CRDTBridge.markNodeInProgress(nodeId),
  removeFromExecution: (noteId, nodeId) => CRDTBridge.removeFromExecution(noteId, nodeId),
  tickWorked: (noteId, nodeId, date, on) => CRDTBridge.tickWorked(noteId, nodeId, date, on),
  setExecutionWorked: (noteId, nodeId, on, dates) => CRDTBridge.setExecutionWorked(noteId, nodeId, on, dates),
  setExecutionPinned: (noteId, nodeId, pinned) => CRDTBridge.setExecutionPinned(noteId, nodeId, pinned),
  getTaskListItems: (noteId) => CRDTBridge.getTaskListItems(noteId),
  setTaskListOrder: (noteId, ids) => CRDTBridge.setTaskListOrder(noteId, ids),
  setExecutionOrder: (noteId, ids) => CRDTBridge.setExecutionOrder(noteId, ids),
  getCarryCandidates: (noteId) =>
    CRDTBridge.getCarryCandidates(noteId, (id) => CRDTBridge.nodeIsDone(Store.find(id))),
  getExecutionStats: (opts) => CRDTBridge.getExecutionStats(opts),
  ensureUnsortedBranch: () => CRDTBridge.ensureUnsortedBranch(),
  onHorizonChange: (partial) => Store.setHorizon(partial),
  // Horizon cut-off predicate for the views (null when no horizon is set).
  getHorizonPredicate: () => {
    const horizon = Store.get("horizon");
    if (!isHorizonActive(horizon)) return null;
    const stats = CRDTBridge.getExecutionStats();
    return buildHorizonPredicate(horizon, {
      ...RoutinesView.horizonContext(stats),
    });
  },
  addNode: (parentId, node) => CRDTBridge.addNode(parentId, node),
  generateId,
  getKanbanColumns: () => CRDTBridge.getKanbanColumns(),
  setKanbanColumns: (cols) => CRDTBridge.setKanbanColumns(cols),

  // Store actions
  onAddNode: (parentId, data, index) => CRDTBridge.addNode(parentId, {
    tags: inProgressTags(CRDTBridge.getTagDefinitions(parentId)), ...data,
  }, index),
  onNodeAdd: (parentId) => openModal(null, {parentId}), // Gantt creation
  onOpenModal: (id) => openModal(id), // Open properties modal directly
  onUpdateNode: (id, updates) => {
    // F1.3: Removed manual view.update - Store subscriber handles it automatically
    // CRDT-first architecture: CRDT write → sync rebuild → Store subscriber → view update
    CRDTBridge.updateNode(id, updates);
  },
  onNodeUpdate: (id, updates) => {
    // F1.3: Removed manual view.update - Store subscriber handles it automatically
    // CRDT-first architecture: CRDT write → sync rebuild → Store subscriber → view update
    CRDTBridge.updateNode(id, updates);
  },
  onDeleteNode: (id) => CRDTBridge.removeNode(id),
  onMoveNode: (id, newParentId, index) =>
    CRDTBridge.moveNode(id, newParentId, index),
  onNodeMove: (id, newParentId, index) =>
    CRDTBridge.moveNode(id, newParentId, index), // Alias for mindmap view
  onNodeReorder: (nodeId, targetSiblingId, position) => {
    // Reorder a node among its siblings
    // Find the parent of both nodes (they should share the same parent)
    const node = Store.find(nodeId);
    const targetSibling = Store.find(targetSiblingId);
    if (!node || !targetSibling) {
      return;
    }

    const parent = Store.findParent(nodeId);
    if (!parent || !parent.children) {
      return;
    }

    // Find current index of both nodes
    const nodeIndex = parent.children.findIndex((c) => c.id === nodeId);
    const targetIndex = parent.children.findIndex(
      (c) => c.id === targetSiblingId,
    );
    if (nodeIndex === -1 || targetIndex === -1) {
      return;
    }

    // Calculate new index
    let newIndex = position === "above" ? targetIndex : targetIndex + 1;

    // If the node is currently before the target, the target index will shift down
    // after removal, so we need to adjust
    if (nodeIndex < targetIndex) {
      newIndex--;
    }

    // Move the node to the new position (same parent)
    CRDTBridge.moveNode(nodeId, parent.id, newIndex);
  },
  onFocusNode: (id) => Store.setFocus(id),
  onSelectNode: (id) => Store.setSelected(id),
  onNodeSelect: (id) => Store.setSelected(id), // Alias for consistency
  onNodeLongPress: (id) => {
    // Long-press on a map node (canvas renderers) opens the mobile node
    // properties panel. Only meaningful while the mobile shell is on screen.
    if (!isMobileShellVisible()) return;
    Store.setSelected(id);
    MobileApp.openNodeProperties(id);
  },
  onNavigate: (id) => {
    Store.setFocus(id);
    switchView(Store.getState().currentView);
  },
  onZoomChange: (zoom) => {
    // Save gantt zoom state to Store
    Store.setGanttZoom(zoom);
  },
  onRowHeightChange: (height) => {
    // Save gantt row height to Store
    Store.setGanttRowHeight(height);
  },
  onMindmapGapChange: (gap) => {
    // Save mindmap gap to Store
    Store.setMindmapGap(gap);
  },
  onSave: () => {
    // Save to localStorage (used by notes view)
    saveToLocalStorage();
  },
  onNodeEdit: (id, mouseX, mouseY) => {
    console.log(
      "[Main] onNodeEdit called:",
      id,
      "view:",
      Store.getState().currentView,
    );
    // Trigger inline editing in map views, or open the properties modal.
    const state = Store.getState();
    if (state.currentView === "bubble") {
      // A leaf's own name (not one of a bubble inside it), else the name of
      // a parent, whose title is drawn on a canvas (interfaces/classic/views/bubble/titles.js):
      // edited in a box at the pointer.
      const leafLabel = document.querySelector(
        `.bubble[data-id="${CSS.escape(id)}"] > .bubble-label-centered .bubble-leaf-label`,
      );
      const node = Store.find(id);
      if (leafLabel) {
        makeEditable(leafLabel, id, "bubble", mouseX, mouseY);
      } else if (node && mouseX !== undefined && mouseY !== undefined) {
        const name = document.createElement("span");
        name.textContent = node.name || node.text || "";
        makeEditable(name, id, "bubble", mouseX, mouseY);
      }
    } else if (state.currentView === "mindmap") {
      // Use setTimeout to wait for any re-render triggered by selection change
      // (double-click fires after click, which triggers setSelected and re-render)
      setTimeout(() => {
        const labelEl = document.querySelector(
          `.mm-node[data-id="${CSS.escape(id)}"] .mm-title`,
        );
        if (labelEl) {
          makeEditable(labelEl, id, "mindmap");
        }
      }, 0);
    } else {
      // Other views use the properties modal.
      openModal(id);
    }
  },

  // List clicks navigate only; the list's pencil handles title editing.
  onNodeOpenFromList: (id) => ViewOrchestrator.selectAndCenterNode(id),

  // Store selectors
  find: Store.find,
  findParent: Store.findParent,
  getAll: Store.getAll,
  getPath: Store.getPath,
  getState: Store.getState,
  isReadOnly: Store.isReadOnly,
  isNodeReadOnly: (id) => CRDTBridge.isNodeReadOnly(id),
  isNodeDeleteBlocked: (id) => CRDTBridge.isNodeDeleteBlocked(id),
  isDocNotesReadOnly: (docId) => CRDTBridge.isDocNotesReadOnly(docId),
  // Links (mobile): ask for access to a "no access" linked map.
  requestLinkedMapAccess: (node) => requestLinkedMapAccess(node, { confirm: false }),

  // Utilities
  escapeHtml,
  generateId,
  getNewColor,

  // Context menu handler
  onNodeContextMenu: (nodeId, x, y, node) => {
    ContextMenu.show(x, y, nodeId, { node });
  },

  // Empty-space (canvas / background) right-click. The target is the
  // currently-focused container — that's the parent of any new content the
  // user pastes or imports here. Falls back to the doc root if no focus is
  // set. The headerLabel makes the destination explicit in views like bubble
  // where the root/focus isn't drawn as a clickable element.
  onCanvasContextMenu: (x, y) => {
    const state = Store.getState();
    const targetId = state?.focusId || state?.data?.id;
    if (!targetId) return;
    const targetNode = Store.find(targetId);
    const targetName = targetNode?.name || state?.data?.name || t("app.fallback.document");
    ContextMenu.show(x, y, targetId, {
      onEmptySpace: true,
      node: targetNode,
      headerLabel: t("app.contextMenu.in", { name: targetName }),
    });
  },

  // CRDT batching (for views to avoid direct sync imports)
  beginBatch: CRDTBridge.beginBatch,
  endBatch: CRDTBridge.endBatch,

  // File attachment support — share-aware: when the user is in a shared
  // doc view these return the active share's docId/DEK, otherwise the
  // main doc's. Attachments wrapped with the active DEK are decryptable
  // by every member of that doc (unified doc_keys model).
  getDocId: () => CRDTBridge.getActiveDocId(),
  // A map on this device has no sync key: its attachments use its own key
  // (attachment-service getAttachmentDek, loaded when the map opens).
  getDek: () => CRDTBridge.getActiveEncryptionKey() || getAttachmentDek(CRDTBridge.getActiveDocId()),
  // Node-aware variants: attachments + tags pasted/edited inside linked
  // content resolve to the LINKED doc's id/DEK so they land in the right
  // blob and remain readable by every member of that doc.
  getDocIdForNode: (nodeId) => CRDTBridge.getDocIdForNode(nodeId),
  getDekForNode: (nodeId) => CRDTBridge.getEncryptionKeyForNode(nodeId) || getAttachmentDek(CRDTBridge.getDocIdForNode(nodeId)),
  // Doc-id-aware DEK lookup — used by the notes tab bar so doc-note
  // attachments are encrypted under the active tab's doc DEK.
  getDekForDoc: (docId) => CRDTBridge.getEncryptionKeyForDoc(docId) || getAttachmentDek(docId),

  // Collaborative notes (Y.XmlFragment per node)
  getNotesFragment: (nodeId) => CRDTBridge.getNodeNotesFragment(nodeId),
  flushNotesRefresh: () => CRDTBridge.flushNotesRefresh(),
  isCollabNotesEnabled: () => CRDTBridge.isCollabNotesEnabled(),

  // Document notes (for notes views to avoid direct sync imports).
  // Each accepts an optional docId to target a linked doc's notes (falls
  // back to the main doc when omitted).
  getDocNotes: (docId) => CRDTBridge.getDocNotes(docId),
  getRelatedDocNotes: (task) => CRDTBridge.getRelatedDocNotes(task),
  showRelatedNotes,
  addDocNote: (note, docId) => CRDTBridge.addDocNote(note, docId),
  removeDocNote: (noteId, docId) => CRDTBridge.removeDocNote(noteId, docId),
  updateDocNote: (noteId, updates, docId) => CRDTBridge.updateDocNote(noteId, updates, docId),
  listDocsWithNotes: () => CRDTBridge.listDocsWithNotes(),
  // Collaborative doc-notes (Y.XmlFragment per docNote).
  getDocNoteFragment: (noteId, docId) => CRDTBridge.getDocNoteFragment(noteId, docId),
  isCollabDocNotesEnabled: (docId) => CRDTBridge.isCollabDocNotesEnabled(docId),
  serializeDocNote: (noteId, docId) => CRDTBridge.serializeDocNote(noteId, docId),
  serializeDocNoteHtml: (noteId, docId) => CRDTBridge.serializeDocNoteHtml(noteId, docId),
  // Canonical (docId, nodeId) resolver — used by refnotes so they carry
  // enough info to navigate across linked docs.
  getNodeLocation: (nodeId) => CRDTBridge.getNodeLocation(nodeId),
  // Used by refnotes when clicked: ensures the target doc's sync is up
  // (or switches main to it) before we try to find the node.
  ensureLinkedDocSync: (docId) => CRDTBridge.ensureLinkedDocSync(docId),

  // Document creation (for import flows). Offline, an online map is kept on
  // this device and goes online once the server can be reached.
  createMap: (name, folderId, initialState) =>
    VaultStorage?.createMap(name, folderId, initialState, { whenOffline: "device" }),
  openDoc: (docId) => DocumentLoader?.handleDocOpen(docId),
  getCurrentFolderId: () => null,

  // Notes overlay controls (for views like markdown that need to hide/restore it)
  isNotesOverlayOpen: () => NotesOverlay.isOpen(),
  hideNotesOverlay: (options) => NotesOverlay.hide(options),
  showNotesOverlay: () => NotesOverlay.show(),

  // Keyboard shortcuts controls (for views that need full keyboard control)
  disableKeyboardShortcuts: () => KeyboardManager.disableKeyboardShortcuts(),
  enableKeyboardShortcuts: () => KeyboardManager.enableKeyboardShortcuts(),

  // Store subscription (for views that need to react to state changes)
  subscribeStore: (callback) => Store.subscribe(callback),

  // Current document ID (for per-doc state persistence)
  getCurrentDocId: () => Store.get("currentDocId"),
};

// ============================================
// View Management
// ============================================

// The notes and markdown views carry the TipTap/ProseMirror editor stack —
// about a third of the old boot bundle — so they load on first activation.
// switchView() handles `module: null` + `load` by fetching the chunk and
// re-running itself; everything else (subscriber updates, destroy) treats a
// missing module as "nothing to do".
const views = {
  notes: { module: null, load: () => import("./views/notes/index.js"), container: null },
  kanban: { module: KanbanView, container: null },
  dashboard: { module: DashboardView, container: null },
  routines: { module: RoutinesView, container: null },
  gantt: { module: GanttView, container: null },
  mindmap: { module: MindmapView, container: null },
  bubble: { module: BubbleView, container: null },
  markdown: { module: null, load: () => import("./views/markdown/index.js"), container: null },
};

// ============================================
// Clipboard (Copy/Cut/Paste)
// ============================================

/**
 * Regenerate all node IDs in a subtree to make it independent.
 * @param {Object} node - Node tree to regenerate IDs for
 * @returns {Object} New node tree with fresh IDs
 */
function regenerateNodeIds(node, depth = 0) {
  if (depth > 500) return { ...node, id: generateId(), children: [] };
  const result = { ...node, id: generateId() };
  if (result.children) {
    result.children = result.children.map(c => regenerateNodeIds(c, depth + 1));
  }
  return result;
}

/**
 * Copy a node subtree to the clipboard.
 * @param {string} nodeId - The node to copy
 */
async function copyNodes(nodeId) {
  const node = Store.find(nodeId);
  if (!node) return;
  const copied = transferClipboardTags(cleanNode(node), CRDTBridge.getTagDefinitions);
  const clipboardData = JSON.stringify({
    _orbital: true,
    version: 2,
    nodes: copied.node,
    tagDefinitions: copied.tagDefinitions,
  });
  try {
    await navigator.clipboard.writeText(clipboardData);
    showToast(t("app.clipboard.copied"), { type: "success" });
  } catch (err) {
    console.error("[Clipboard] Failed to copy:", err);
    showToast(t("app.clipboard.copyFailed"), { type: "error" });
  }
}

/**
 * Cut a node subtree (copy + delete).
 * @param {string} nodeId - The node to cut
 */
async function cutNodes(nodeId) {
  const node = Store.find(nodeId);
  if (!node) return;
  const copied = transferClipboardTags(cleanNode(node), CRDTBridge.getTagDefinitions);
  const clipboardData = JSON.stringify({
    _orbital: true,
    version: 2,
    nodes: copied.node,
    tagDefinitions: copied.tagDefinitions,
  });
  try {
    await navigator.clipboard.writeText(clipboardData);
    CRDTBridge.removeNode(nodeId);
    showToast(t("app.clipboard.cut"), { type: "success" });
  } catch (err) {
    console.error("[Clipboard] Failed to cut:", err);
    showToast(t("app.clipboard.cutFailed"), { type: "error" });
  }
}

/**
 * Paste nodes from clipboard as children of the target node.
 * @param {string} parentId - The node to paste into
 */
async function pasteNodes(parentId) {
  const destinationDocId = CRDTBridge.getDocIdForNode(parentId);
  try {
    const text = await navigator.clipboard.readText();
    if (!text || text.length > 10_000_000) return; // 10MB safety limit
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      // Not JSON — ignore silently (user may have non-Orbital content)
      return;
    }
    if (!data._orbital || !data.nodes) return;
    if (destinationDocId !== CRDTBridge.getDocIdForNode(parentId) || !Store.find(parentId)) {
      showToast(t("app.clipboard.destinationChanged"), { type: "warning" });
      return;
    }
    const cleaned = cleanNode(data.nodes);
    const plainLinks = keepOnlyValidPastedLinks(cleaned, destinationDocId);
    const newNodes = regenerateNodeIds(cleaned);
    const definitions = Array.isArray(data.tagDefinitions) ? data.tagDefinitions : [];
    if (!CRDTBridge.pasteNode(parentId, newNodes, definitions)) return;
    showToast(plainLinks > 0
      ? t("app.clipboard.pastedPlainLinks", { count: plainLinks })
      : t("app.clipboard.pasted"), { type: plainLinks > 0 ? "warning" : "success" });
  } catch (err) {
    console.error("[Clipboard] Failed to paste:", err);
    showToast(t("app.clipboard.pasteFailed"), { type: "error" });
  }
}

/**
 * A pasted link stays a link only where "Link document here" would allow it:
 * directly in the open map, to another map, not already linked there. Other
 * links become plain items named after the linked map (nothing is dropped).
 * Mutates `tree`.
 * @returns {number} number of links turned into plain items
 */
function keepOnlyValidPastedLinks(tree, destinationDocId) {
  const hostDocId = CRDTBridge.getCurrentDocId();
  const linked = collectLinkedDocIds(Store.getState()?.data);
  let converted = 0;
  (function walk(node) {
    if (!node) return;
    if (node.linkedDocId) {
      const ok = destinationDocId === hostDocId
        && node.linkedDocId !== hostDocId
        && !linked.has(node.linkedDocId);
      if (ok) {
        linked.add(node.linkedDocId);
      } else {
        node.name = node.linkedDocTitle || node.name || t("app.link.plainItemName");
        delete node.linkedDocId;
        delete node.linkedDocTitle;
        converted++;
      }
    }
    (node.children || []).forEach(walk);
  })(tree);
  return converted;
}

// ============================================
// Document References (Link / Unlink)
// ============================================

/**
 * Link another document into the current doc. Creates a new child "stub"
 * node under `parentId` carrying only {linkedDocId, linkedDocTitle}. The
 * linked doc's root is rendered at the stub's position by compositeLinkedDocs.
 *
 * Does not transform the selected node — the selected node becomes the parent
 * of the new stub child. This keeps pre-existing content intact.
 *
 * @param {string} parentId - The node under which to attach the linked doc
 */
async function linkDocument(parentId) {
  if (!VaultStorage) {
    showToast(t("app.link.vaultUnavailable"), { type: "error" });
    return;
  }

  // Runtime guard: refuse to embed a stub inside a shared doc.
  // The linkedDocId would be sent to every collaborator over CRDT,
  // but they wouldn't have the wrappedDEK — so the stub renders as
  // "(no access)" and leaks the target doc's UUID + cached title.
  if (Store.get("isShared")) {
    showToast(t("app.link.notInShared"), { type: "error" });
    return;
  }
  // Items of a linked map belong to that map: a link added there would be
  // written into the other map, not this one.
  const hostDocId = CRDTBridge.getCurrentDocId();
  if (!hostDocId || CRDTBridge.getDocIdForNode(parentId) !== hostDocId) {
    showToast(t("app.link.notInLinkedMap"), { type: "error" });
    return;
  }

  // Collect docIds already referenced by any stub in the main tree so the
  // picker can exclude them (C4: no duplicate links in the same main doc).
  const alreadyLinked = collectLinkedDocIds(Store.getState()?.data);
  // Maps stored only on this device have no server channel: a link to them
  // could never load.
  const localOnly = (VaultStorage.getIndex?.()?.docs || [])
    .filter((d) => d._storage === "local")
    .map((d) => d.docId);

  const pickerResult = await VaultUI.openPicker({
    title: t("app.link.pickerTitle"),
    excludeDocId: hostDocId,
    excludeDocIds: [...alreadyLinked, ...localOnly],
  });

  if (!pickerResult || !pickerResult.docId) return;

  const docId = pickerResult.docId;
  const title = pickerResult.title || t("common.untitled");

  // Backstop: re-check for duplicates in case state changed between the
  // picker opening and the user confirming, or if this function is ever
  // called programmatically bypassing the picker's exclusion list.
  if (alreadyLinked.has(docId) || collectLinkedDocIds(Store.getState()?.data).has(docId)) {
    showToast(t("app.link.alreadyLinked", { title }), { type: "warning" });
    return;
  }

  // Members of this map will see the link's title (not its content, unless
  // they also have access to the linked map): ask first.
  if (await mayHaveOtherMembers(hostDocId, VaultStorage)) {
    const ok = await confirmModal({
      title: t("app.link.sharedTitle"),
      message: t("app.link.sharedMessage", { title }),
      confirmText: t("app.link.sharedConfirm"),
    });
    if (!ok) return;
  }

  // Kick off sync for the linked doc up-front so content is available as
  // soon as the stub node is created (avoids a visible "unavailable" flash).
  CRDTBridge.ensureLinkedDocSync(docId);

  // Create a new child stub under parentId. The stub carries only the link
  // reference — all visible content comes from the linked doc's root.
  CRDTBridge.addNode(parentId, {
    id: generateId(),
    name: title,
    linkedDocId: docId,
    linkedDocTitle: title,
    children: [],
  });

  // Track this link in the vault so share-revocation can migrate stubs
  // later without scanning every doc. Best-effort — failures don't block.
  VaultStorage?.recordLink?.(docId, hostDocId).catch(() => {});

  showToast(t("app.link.linked", { title }), { type: "success" });
}

/**
 * True when the user owns `docId` online and has shared it with someone.
 * Unknown (offline, error) counts as shared, so the user is asked.
 */

/** Walk the composited tree and return every docId referenced by a stub. */
function collectLinkedDocIds(tree) {
  const ids = new Set();
  (function walk(node) {
    if (!node) return;
    if (node.linkedDocId) ids.add(node.linkedDocId);
    if (node.children) node.children.forEach(walk);
  })(tree);
  return ids;
}

/**
 * Unlink a linked document from the current doc: removes the stub from the
 * main tree and stops the linked doc's sync channel. The linked document
 * itself stays alive in the user's vault.
 * @param {string} nodeId - The stub node id (the attachment point)
 */
/**
 * Ask the owner of a linked map for access (a "no access" link): confirm,
 * then send the request. Used by the badge on desktop and by mobile (which
 * confirms in its own sheet first: `confirm: false`).
 * @param {Object|null} node - the link node
 * @param {{confirm?: boolean}} [options]
 */
async function requestLinkedMapAccess(node, { confirm = true } = {}) {
  const docId = node?.linkedDocId;
  if (!docId) return;
  const title = node.linkedDocTitle || node.name || t("app.fallback.thisDocument");
  if (confirm) {
    const ok = await confirmModal({
      title: t("app.requestAccess.title"),
      message: t("app.requestAccess.message", { title }),
      confirmText: t("app.requestAccess.title"),
    });
    if (!ok) return;
  }
  // The title stays on this device (S-M1): the owner sees their own title.
  const result = await AccessRequests.requestAccess(docId, VaultStorage?.getDoc?.(docId)?.wrappedDEK || null);
  if (result.ok) {
    showToast(t("app.requestAccess.sent", { title }), { type: "success" });
  } else {
    showToast(t("app.requestAccess.failed", { error: result.error || t("app.requestAccess.tryLater") }), { type: "error" });
  }
}

async function unlinkDocument(nodeId) {
  const node = Store.find(nodeId);
  if (!node || !node.linkedDocId) return;

  const linkedDocId = node.linkedDocId;
  const title = node.linkedDocTitle || t("app.fallback.document");

  const ok = await confirmModal({
    title: t("app.unlink.title"),
    message: t("app.unlink.message", { title }),
    confirmText: t("app.unlink.confirm"),
  });
  if (!ok) return;

  // removeNode on a stub routes to the stub's host doc (unlink = delete the
  // stub entry). The linked doc itself stays — it lives in the vault.
  CRDTBridge.removeNode(nodeId);

  // Only tear down the linked doc's sync channel (and drop the vault
  // linkers entry) if no other stub in the tree still references it. Run
  // after the removal has propagated through the rebuild so the tree
  // reflects the deletion.
  queueMicrotask(() => {
    const state = Store.getState();
    const stillReferenced = _treeHasLinkedDoc(state?.data, linkedDocId);
    if (!stillReferenced) {
      CRDTBridge.stopLinkedDoc?.(linkedDocId);
      const hostDocId = CRDTBridge.getCurrentDocId();
      if (hostDocId) {
        VaultStorage?.removeLink?.(linkedDocId, hostDocId).catch(() => {});
      }
    }
  });

  showToast(t("app.unlink.done", { title }), { type: "success" });
}

/** Walk the composited tree looking for any stub pointing at docId. */
function _treeHasLinkedDoc(node, docId) {
  if (!node) return false;
  if (node.linkedDocId === docId) return true;
  if (!node.children) return false;
  for (const child of node.children) {
    if (_treeHasLinkedDoc(child, docId)) return true;
  }
  return false;
}

// switchView, updateViewerHints, updateReadOnlyBadge moved to ViewOrchestrator
function switchView(viewName) {
  // Another interface is shown: classic draws nothing (interfaces/index.js).
  if (!isClassicShown()) return;
  TagPopup.close();
  ViewOrchestrator.switchView(viewName);
}

// renderTree moved to ViewOrchestrator
function renderTree() {
  if (!isClassicShown()) return;
  ViewOrchestrator.renderTree();
}

// ============================================
// Local Storage (delegates to DocumentManager)
// ============================================

// Initialize ViewOrchestrator with callbacks
ViewOrchestrator.setupViewOrchestrator({
  beginBatch: () => CRDTBridge.beginBatch(),
  endBatch: () => CRDTBridge.endBatch(),
  Store,
  views,
  sharedCallbacks,
  hideNotesOverlay: (options) => NotesOverlay.hide(options),
  updateNode: (id, updates) => CRDTBridge.updateNode(id, updates),
  moveNode: (id, newParentId, index) => CRDTBridge.moveNode(id, newParentId, index),
});

// Initialize SyncOrchestrator with callbacks
SyncOrchestrator.setupSyncOrchestrator({
  getCurrentUser,
  CRDTBridge,
  UserCRDTBridge,
  AuthHandlers,
  Store,
  VaultStorage,
  VaultUI,
  // Lazy view: not loaded ⇒ it can't be open, so isOpen defaults to false.
  NotesView: {
    isOpen: () => views.notes.module?.isOpen?.() ?? false,
    refresh: () => views.notes.module?.refresh?.(),
  },
  NotesOverlay,
  confirmModal,
  showToast,
  switchView,
  markSaved,
  handleDocOpen: DocumentLoader.handleDocOpen,
});

// Initialize DocumentManager with callbacks
DocumentManager.init({
  getState: () => Store.getState(),
  get: (key) => Store.get(key),
  saveDocState,
  getCurrentUser,
  cleanNodeForSync: SyncOrchestrator.cleanNodeForSync,
});

// Initialize ShareManager with callbacks
ShareManager.init({
  getCurrentUser,
  Store,
  VaultStorage,
  CRDTBridge,
  Keypair,
  getKeypairManager: () => keypairManager,
  isDocumentLoading: DocumentManager.isDocumentLoading,
});

// A viewer's refused changes (schema v2 plan, decision 4): dropped, or kept
// as a copy, then the map reloaded from the server.
initRefusedChanges({
  getCurrentDocId: () => Store.get("currentDocId"),
  getMapName: (docId) => VaultStorage.getDoc?.(docId)?.title || "",
  stopSync: closeMap,
  reopen: (docId) => DocumentLoader.handleDocOpen(docId),
  copyMap: async (docId, options) => (await import("../../engine/services/map-copy.js")).copyMap(VaultStorage, docId, options),
});

// How this interface shows what the engine needs to show (notices,
// questions, progress): as before the engine stopped doing it itself.
installEngineHost();

// Initialize DocumentLoader with callbacks
DocumentLoader.init({
  getDocState,
  getLastOpenedDoc,
  setLastOpenedDoc,
  switchView,
  markSaved,
  VaultStorage,
  VaultUI,
  Store,
  restoreSidebarState: (docState) => SidebarManager.restoreState(docState),
});

// Vault repair, once per session when the vault has synced: owner proofs
// for the vault's maps, and owned maps missing from the vault put back.
scheduleVaultRepair({
  getMasterKey: () => AuthManager.getMasterKey(),
  vault: VaultStorage,
  userCRDT: UserCRDTBridge,
  notify: (titles) => showToast(t("vault.repair.restored", {
    count: titles.length,
    titles: titles.map((title) => `“${title || t("common.untitled")}”`).join(", "),
  }), { type: "info", duration: 10000 }),
});

// Initialize AuthManager with callbacks
// Note: Some callbacks reference functions defined below (forward references work due to hoisting)
AuthManager.init({
  setCurrentUser: (user) => {
    setCurrentUser(user);
    VaultUI.updateVaultButton(!!user);
  },
  getCurrentUser,
  initializeAppCore,
  setupCRDTEventListeners: SyncOrchestrator.setupCRDTEventListeners,
  initVaultStorage,
  enableUserCRDT,
  VaultStorage,
  VaultUI,
  initializeLoggedOutMode,
  initShareModule,
  initInvitations,
  hydrateAccessRequests: () => AccessRequests.hydrate()
    .then(() => AccessRequests.noteVaultKeys(VaultStorage?.getIndex?.()?.docs)),
  destroyInvitations,
  destroyShareModule,
  disableUserCRDT,
  getLastOpenedDoc,
  setLastOpenedDoc,
  getDocState,
  stopCRDTSync: closeMap,
  CRDTBridge,
  Store,
  switchView,
  markSaved,
  clearDeltaSyncState,
  cleanupCRDTEventListeners: SyncOrchestrator.cleanupCRDTEventListeners,
  updateSyncStatus: SyncOrchestrator.updateSyncStatus,
  ConnectionState,
  showNotification: (msg, type) => showToast(msg, { type }),
  generateId,
  ROOT_COLOR,
  renderTree,
  AuthHandlers,
  showDeletionModal,
  Keypair,
  getKeypairManager: () => keypairManager,
  isDocumentLoading: DocumentManager.isDocumentLoading,
  setIsDocumentLoading: DocumentManager.setIsDocumentLoading,
  showInvitationsModal,
  DocumentLoader,
});

// Initialize ProfileManager with callbacks
ProfileManager.init({
  AuthHandlers,
  showScreen,
  showDeletionModal,
});

// Initialize OfflineMode with callbacks
OfflineMode.setupOfflineMode({
  initializeAppCore,
  initVaultStorage,
  enableUserCRDT,
  VaultStorage,
  VaultUI,
  CRDTBridge,
  Store,
  getOrCreateLocalUser,
  getDocState,
  restoreSidebarState: (docState) => SidebarManager.restoreState(docState),
  getLastOpenedDoc,
  setLastOpenedDoc,
  clearLastOpenedDoc,
  stopCRDTSync: closeMap,
  generateId,
  ROOT_COLOR,
  markSaved,
  renderTree,
  switchView,
});

/**
 * Save current state to local storage.
 * Wrapper that delegates to DocumentManager.
 */
async function saveToLocalStorage() {
  await DocumentManager.saveToLocalStorage();
}

// Debounced wrapper for the per-store-change save. The save is a synchronous
// localStorage JSON write of per-device UI state — running it inside the
// render rAF for EVERY store change (every keystroke) is main-thread I/O for
// state that rarely changes. Trailing debounce + flush on hide/unload, so no
// pending write is ever dropped (rule #6).
let _localSaveTimer = null;
const LOCAL_SAVE_DEBOUNCE_MS = 500;

function scheduleLocalStateSave() {
  if (_localSaveTimer) return;
  _localSaveTimer = setTimeout(() => {
    _localSaveTimer = null;
    saveToLocalStorage();
  }, LOCAL_SAVE_DEBOUNCE_MS);
}

function flushLocalStateSave() {
  if (_localSaveTimer) {
    clearTimeout(_localSaveTimer);
    _localSaveTimer = null;
  }
  saveToLocalStorage();
}

// ============================================
// Initialization
// ============================================

// Track if core initialization has been done
let _coreInitialized = false;

/**
 * Initialize the core app infrastructure (views, modal, event handlers).
 * This is called once and sets up everything except data loading.
 * Safe to call multiple times - will only run once.
 */
async function initializeAppCore() {
  if (_coreInitialized) {
    console.log("[Orbital] Core already initialized, skipping");
    return;
  }

  console.log("[Orbital] Initializing core app infrastructure...");

  // Initialize inline editor with Store/CRDT callbacks
  setupInlineEditor({
    findNode: (id) => Store.find(id),
    updateNode: (id, updates) => CRDTBridge.updateNode(id, updates),
  });

  // Get view containers
  initTaskPostponement({ onChange: () => {
    if (isMobileShellVisible()) return;
    ModalManager.refreshTaskPostponement();
    const state = Store.getState();
    if (state.currentView === 'notes') views.notes.module?.update?.(state, state);
    if (NotesOverlay.isOpen()) NotesOverlay.update(state);
  } });
  RoutinesView.initRoutines({onNodeKindsChanged:()=>views[Store.get('currentView')]?.module?.refreshRoutineNodes?.()});
  views.routines.container = document.getElementById("routines-view");
  views.notes.container = document.getElementById("notes-view");
  views.kanban.container = document.getElementById("kanban-view");
  views.dashboard.container = document.getElementById("dashboard-view");
  views.gantt.container = document.getElementById("gantt-view");
  views.mindmap.container = document.getElementById("mindmap-view");
  views.bubble.container = document.getElementById("bubble-view");
  views.markdown.container = document.getElementById("markdown-view");

  // Wire up view tabs
  document.querySelectorAll(".tab").forEach((tab) => {
    tab.addEventListener("click", () => {
      const viewName = tab.dataset.view;
      switchView(viewName);
    });
  });

  // Initialize sidebar manager (resize + collapse persistence)
  SidebarManager.init({
    getDocId: () => Store.get('currentDocId'),
    saveDocState,
    getSidebarCollapsed: () => Store.get('sidebarCollapsed'),
    setSidebarCollapsed: (value) => Store.setSidebarCollapsed(value),
  });

  // Wire up sidebar toggle
  const sidebarToggle = document.getElementById("sidebar-toggle");
  if (sidebarToggle) {
    sidebarToggle.addEventListener("click", () => {
      SidebarManager.toggleCollapsed();
    });
  }

  // Initialize sidebar tags section
  // Configure Store's "is node done" check to use tag-based system
  Store.setNodeIsDoneFn((node) => CRDTBridge.nodeIsDone(node));

  SidebarTags.init({
    getTagDefinitions: (nodeId) => CRDTBridge.getTagDefinitions(nodeId),
    getTagCategories: () => CRDTBridge.getTagCategories(),
    addTagDefinition: (tag) => CRDTBridge.addTagDefinition(tag),
    updateTagDefinition: (id, updates) => CRDTBridge.updateTagDefinition(id, updates),
    deleteTagDefinition: (id) => CRDTBridge.deleteTagDefinition(id),
    getHiddenTagIds: () => Store.get('hiddenTagIds'),
    getHighlightedTagIds: () => Store.get('highlightedTagIds'),
    setHiddenTagIds: (ids) => Store.setHiddenTagIds(ids),
    setHighlightedTagIds: (ids) => Store.setHighlightedTagIds(ids),
    getTagDimLevel: () => Store.get('tagDimLevel'),
    setTagDimLevel: (level) => Store.setTagDimLevel(level),
    generateId,
  });

  // Execution changes (ticks, sends, pins) are not tree changes, so the Store
  // does not re-render views for them. The views that draw the execution
  // layer refresh explicitly.
  on(Events.EXECUTION_CHANGED, () => {
    const view = Store.get("currentView");
    if (view === "gantt" || view === "dashboard") {
      const state = Store.getState();
      views[view]?.module?.update?.(state, state);
    } else if (HORIZON_VIEWS.has(view) && isHorizonActive(Store.get("horizon"))) switchView(view);
  });

  // Routine completion and edits do not rebuild the tree. Local changes need
  // the same task-list refresh that remote execution changes already receive.
  on(Events.ROUTINES_CHANGED, ({origin}) => {
    if (origin !== 'local') return;
    const state = Store.getState();
    if (Store.get('currentView') === 'notes') views.notes?.module?.update?.(state, state);
    if (NotesOverlay.isOpen()) NotesOverlay.update(state);
  });

  // Initialize tag popup
  TagPopup.init({
    getTagDefinitions: (nodeId) => CRDTBridge.getTagDefinitions(nodeId),
    addTagDefinition: (tag) => { CRDTBridge.addTagDefinition(tag); SidebarTags.render(); },
    toggleNodeTag: (nodeId, tagId) => CRDTBridge.toggleNodeTag(nodeId, tagId),
    setNodesTag: (nodeIds, tagId, on) => CRDTBridge.setNodesTag(nodeIds, tagId, on),
    nodeHasTag: (node, tagId) => CRDTBridge.nodeHasTag(node, tagId),
    generateId,
    find: (id) => Store.find(id),
  });

  // Multi-selection (desktop views and lists). It stays across view and theme
  // switches (only Esc, ✕ or a click elsewhere ends it), but not across maps:
  // node ids can repeat between maps (recovery copies, a linked map opened on
  // its own). Deleted nodes drop out of it.
  MultiSelect.init({
    getState: () => Store.getState(),
    isReadOnly: () => Store.isReadOnly(),
    moveNodes: (ids, parentId, index) => CRDTBridge.moveNodes(ids, parentId, index),
    openTags: (ids, x, y) => TagPopup.openMany(ids, x, y),
    isTagPopupOpen: () => TagPopup.isOpen(),
    isModalOpen: () => isModalOpen(),
    isEnabled: () => !isMobileShellVisible() && isClassicShown(),
    notify: (message) => showToast(message, { type: "warning" }),
    ganttRowIds: () => GanttView.rowIds(),
  });
  Store.subscribe((state, previous) => {
    if (!MultiSelect.isActive()) return;
    if (previous && state.currentDocId !== previous.currentDocId) {
      MultiSelect.clear();
      return;
    }
    const kept = MultiSelect.getIds().filter((id) => Store.find(id));
    // (set() also drops a node that became the focus: the view's root.)
    if (kept.length !== MultiSelect.getIds().length || state.focusId !== previous?.focusId) MultiSelect.set(kept);
    else if (previous && state.currentView !== previous.currentView) MultiSelect.refresh();
  });


  // Prevent clicks in sidebar footer sections from propagating to sidebar collapse handler
  document.querySelectorAll(".sidebar-section-stop-propagation").forEach(el => {
    el.addEventListener("click", e => e.stopPropagation());
  });

  // Notes view temporarily covers the side panel; retain the user's open
  // preference so returning to a map restores it.
  let lastNotesPanelVisible = null;
  Store.subscribe((state) => {
    const visible = state.notesPanelOpen && state.currentView !== "notes";
    if (visible === lastNotesPanelVisible) return;
    lastNotesPanelVisible = visible;
    if (visible) NotesOverlay.show();
    else NotesOverlay.hide({ preservePreference: true });
  });

  // A live role change (the owner made us a viewer, or an editor again):
  // update the "View only" badge and the shortcut hints at once, not at the
  // next view switch.
  Store.subscribe((state, previous) => {
    if (previous && (state.shareRole !== previous.shareRole || state.isShared !== previous.isShared)) {
      ViewOrchestrator.updateViewerHints();
    }
    // Another map opened: the server indicator follows the new map's
    // connection (or the vault's, for a map on this device).
    if (previous && state.currentDocId !== previous.currentDocId) {
      SyncOrchestrator.updateSyncStatus(CRDTBridge.getConnectionState());
    }
  });

  // Setup undo/redo toolbar buttons
  initUndoRedoToolbar();

  // Setup modal
  initModals();
  ModalManager.setupModal({
    canPostponeTask: CRDTBridge.canPostponeTask,
    getTaskPostponement: CRDTBridge.getTaskPostponement,
    isReadOnly: () => Store.isReadOnly(),
    showToast,
    getState: () => Store.getState(),
    get: (key) => Store.get(key),
    findNode: (id) => Store.find(id),
    findParent: (id) => Store.findParent(id),
    getAll: () => Store.getAll(),
    getPath: (id) => Store.getPath(id),
    updateNode: (id, updates) => CRDTBridge.updateNode(id, updates),
    addNode: (parentId, node) => CRDTBridge.addNode(parentId, node),
    moveNode: (id, newParentId, index) => CRDTBridge.moveNode(id, newParentId, index),
    setSelected: (id) => Store.setSelected(id),
    switchView,
    showModalGeneric,
    closeModalGeneric,
    generateId,
    getThemeApi,
    getColors: () => getCurrentPalette(),
    beginBatch: () => CRDTBridge.beginBatch(),
    endBatch: () => CRDTBridge.endBatch(),
    getDocId: () => CRDTBridge.getActiveDocId(),
    getDocIdForNode: (id) => CRDTBridge.getDocIdForNode(id),
    getDek: () => CRDTBridge.getActiveEncryptionKey() || getAttachmentDek(CRDTBridge.getActiveDocId()),
    getNotesFragment: (nodeId) => CRDTBridge.getNodeNotesFragment(nodeId),
    flushNotesRefresh: () => CRDTBridge.flushNotesRefresh(),
    isCollabNotesEnabled: () => CRDTBridge.isCollabNotesEnabled(),
    getTagDefinitions: (nodeId) => CRDTBridge.getTagDefinitions(nodeId),
  });

  // Initialize notes overlay panel
  const mainContainer =
    document.querySelector(".app-container") || document.body;
  NotesOverlay.init(mainContainer, Store.getState(), {
    ...sharedCallbacks,
    onSave: () => {
      saveToLocalStorage();
    },
    onPanelToggle: (isOpen) => {
      Store.setNotesPanelOpen(isOpen);
      saveToLocalStorage();
    },
    showHint: (message) => {
      const hint = document.getElementById("hint");
      if (hint) {
        hint.textContent = message;
        hint.classList.add("visible");
        setTimeout(() => {
          hint.classList.remove("visible");
        }, 1500);
      }
    },
  });

  // Initialize the dedicated mobile shell. It renders into #mobile-app only
  // when the main app screen is visible and mobile media queries apply.
  MobileApp.init({
    Store,
    VaultStorage,
    sharedCallbacks,
    getCurrentUser,
    getColors: () => getCurrentPalette(),
    openDoc: (docId) =>
      getCurrentUser()
        ? DocumentLoader.handleDocOpen(docId)
        : handleLoggedOutDocOpen(docId),
    createDoc: async (title, folderId, initialState) => {
      const result = await VaultStorage?.createMap(title, folderId, initialState, { whenOffline: "device" });
      if (result?.docId) {
        // Decouple "create succeeded" from "open succeeded" — if open throws,
        // the doc is in the vault but the UI is stale. Surface the open
        // failure separately so the caller can still see that the doc was
        // created (and not retry the create blindly).
        try {
          if (getCurrentUser()) {
            await DocumentLoader.handleDocOpen(result.docId);
          } else {
            await handleLoggedOutDocCreate(result.docId);
          }
        } catch (openErr) {
          console.error("[Main] createDoc: open after create failed:", openErr);
          showToast?.(t("app.doc.createdNotOpened"), { type: "warning" });
        }
      }
      return result;
    },
    renameDoc: async (docId, title) => {
      await VaultStorage?.renameMap(docId, title);
      if (VaultStorage?.getCurrentDocId?.() === docId) {
        const rootId = Store.getState()?.data?.id;
        if (rootId) {
          CRDTBridge.updateNode(rootId, { name: title });
        }
      }
    },
    moveDoc: async (docId, folderId) => {
      await VaultStorage?.moveMap(docId, folderId);
    },
    deleteDoc: async (docId) => {
      const deletingCurrentDoc = VaultStorage?.getCurrentDocId?.() === docId;
      await VaultStorage?.deleteDoc(docId);
      // Its files attached offline and not sent go with it.
      await deleteWaitingFiles([docId]).catch(() => {});
      // Drop any unsaved-edit stashes that referenced the now-deleted doc —
      // they'd never recover (the target doc is gone) and would just sit
      // in localStorage until the 7d TTL expired.
      dropMobileStashEntriesForDoc(docId);
      if (!getCurrentUser()) {
        await handleLoggedOutDocDelete(docId);
      } else if (deletingCurrentDoc) {
        // Prefer an owner doc as the next destination so the user doesn't
        // get bumped into a read-only shared doc by the simple docs[0] pick.
        // Falls back to whatever the index has if no owner docs remain.
        const docs = VaultStorage?.getIndex?.()?.docs || [];
        const nextDocId = (docs.find((d) => !d.role || d.role === "owner") || docs[0])?.docId;
        if (nextDocId) {
          await DocumentLoader.handleDocOpen(nextDocId);
        } else {
          await clearActiveDocumentAfterDelete();
        }
      }
    },
    createFolder: async (name, parentId) => {
      await VaultStorage?.createFolder(name, parentId);
    },
    renameFolder: async (folderId, name) => {
      await VaultStorage?.renameFolder(folderId, name);
    },
    deleteFolder: async (folderId, moveContentsTo) => {
      await VaultStorage?.deleteFolder(folderId, moveContentsTo);
    },
    showLogin: () => {
      const loginToSyncBtn = document.getElementById("login-to-sync-btn");
      if (loginToSyncBtn) {
        loginToSyncBtn.click();
      } else {
        showScreen("login-screen");
      }
    },
    getUITheme: () => getUITheme(),
    setUITheme: (theme) => setUITheme(theme),
    logout: (options) => AuthHandlers.logout(options),
    // Account actions the mobile shell previously had no access to. Without
    // these, "Profile → Security → Regenerate" (which the recovery-upgrade
    // prompt tells users to use) and account deletion were desktop-only.
    regenerateRecoveryPhrase: () => AuthForms.regenerateRecoveryPhrase(),
    showDeletionModal,
    // Share invitations. The notifications module is already initialised on
    // mobile (auth-manager does it for both shells) and keeps the list in
    // sync over the user-WS — mobile simply had no UI for it, so a
    // mobile-only user could never accept an invite.
    getInvitations,
    acceptInvitation,
    declineInvitation,
    ignoreInviter,
    // Sharing state + management for the mobile doc-properties sheet.
    // Server-side role enforcement is authoritative; the client gating in
    // the sheet is UX only.
    fetchShareDetails,
    shareInviteUser,
    shareUpdateUserRole,
    shareRemoveUser,
    shareCancelInvitation,
    shareUnshare,
    // The map's link (docs/plans/2026-10-28-link-sharing.md).
    shareLink,
    // Owner restores a member's access after their encryption identity repair.
    // Restoring access needs the member's new key verified out of band
    // (A2): the dialog shows both fingerprints and requires confirmation.
    shareRestoreMemberAccess: async (docId, userId, username) => {
      const { confirmAndRestoreMemberAccess } = await import('./ui/identity-repair.js');
      return confirmAndRestoreMemberAccess(docId, userId, username);
    },
    // Generic notifications (share_revoked etc.) + the shared recovery flow.
    getPendingNotifications,
    recoverRevokedShare,
    markNotificationRead,
    // Canvas-mode bubble view has no DOM bubbles to measure — the mobile
    // fit-to-view falls back to these physics-derived world bounds.
    getBubbleContentBounds: () => BubbleView.getContentBounds?.() || null,
    getMindmapContentBounds: () => MindmapView.getContentBounds?.() || null,
    renderDesktopView: (viewName) => {
      const cfg = views[viewName];
      if (!cfg?.module?.render || !cfg.container) return null;
      const state = Store.getState();
      if (!state?.data) return null;
      try {
        cfg.module.render(cfg.container, state, sharedCallbacks);
      } catch (err) {
        console.error(`[Mobile] desktop view ${viewName} render failed:`, err);
        return null;
      }
      return cfg.container;
    },
    destroyDesktopView: (viewName) => {
      const cfg = views[viewName];
      try { cfg?.module?.destroy?.(); } catch (e) {}
    },
    refreshVault: () => {
      // Best-effort manual refresh: emit the vault index updated event so
      // mobile re-reads and any wired sync triggers can react. For online
      // users the WebSocket already keeps things live; this is mostly for
      // visual confirmation + future hook for explicit re-fetch.
      try {
        emit(Events.VAULT_INDEX_UPDATED, { source: "mobile-pull-refresh" });
      } catch (e) {}
    },
    setDesktopViewTransform: (viewName, transform, options = {}) => {
      // commitToStore=false updates only the mounted desktop view state. Mobile
      // previews use this so pan/pinch/fit gestures cannot overwrite the saved
      // desktop mindmap/bubble transforms.
      // NaN/range guard: a degenerate gesture (zero distance pinch, missing
      // rect) can produce non-finite or extreme numbers; refuse to forward
      // them since they'd corrupt the view state and persist to Store. The
      // zoom is kept within [0.0005, 3] so an out-of-range value can't poison
      // Store either; the floor is only a sanity bound: a fit goes as low as
      // the map needs (a 20,000-item bubble map: about 0.003 on a phone).
      const panX = Number(transform?.panX);
      const panY = Number(transform?.panY);
      const zoom = Number(transform?.zoom);
      if (!Number.isFinite(panX) || !Number.isFinite(panY) || !Number.isFinite(zoom)) {
        return;
      }
      const clampedZoom = Math.max(0.0005, Math.min(3, zoom));
      const safe = { panX, panY, zoom: clampedZoom };
      const commitToStore = options.commitToStore !== false;
      // Mark preview-owned transforms. The views' render() consults this
      // flag (while the mobile shell is visible) to keep the user's live
      // pan/zoom across re-renders instead of snapping back to the
      // desktop-saved Store transform — a full render used to reset the
      // mobile view on every remote edit or shell remount.
      if (!commitToStore) safe._mobilePreview = true;
      if (viewName === "mindmap") {
        MindmapState.setMState(safe);
        MindmapZoomPan.updateTransform({ save: false });
        if (commitToStore) {
          MindmapState.setLastSavedTransform({ ...safe });
          Store.setMindmapTransform(safe);
        }
      } else if (viewName === "bubble") {
        BubbleState.setBState(safe);
        BubbleState.updateTransform({ save: false });
        if (commitToStore) {
          BubbleState.setLastSavedTransform({ ...safe });
          Store.setBubbleTransform(safe);
        }
      }
    },
    getDesktopViewTransform: (viewName) => {
      // Live (session) transform of a map view — the mount layer uses it to
      // stash/restore the user's framing across tab switches (unmount runs
      // the view's destroy(), which resets it).
      const tf = viewName === "mindmap"
        ? MindmapState.mState
        : viewName === "bubble" ? BubbleState.getBState() : null;
      if (!tf || !Number.isFinite(tf.zoom)) return null;
      return { panX: tf.panX, panY: tf.panY, zoom: tf.zoom, _mobilePreview: !!tf._mobilePreview };
    },
    saveToLocalStorage,
    showToast,
  });

  MapSearch.initSearch({
    getAll: Store.getAll,
    getPath: Store.getPath,
    getDocId: () => Store.get('currentDocId'),
    zoomTo: id => ViewOrchestrator.selectAndCenterNode(id),
    getViewContainer: () => views[Store.get('currentView')]?.container,
    showSidebar: () => SidebarManager.setCollapsed(false),
  });
  MapSearch.setupSearchUI({input: document.getElementById('search-input'), results: document.getElementById('search-results')});
  Store.subscribe((state, previous) => {
    if (state.data !== previous.data || state.currentDocId !== previous.currentDocId) MapSearch.refreshSearch();
  });

  // Setup keyboard shortcuts
  KeyboardManager.setupKeyboardShortcuts({
    isActive: isClassicShown,
    isModalOpen,
    getState: () => Store.getState(),
    isReadOnly: () => Store.isReadOnly(),
    isDeleteBlocked: () => Store.isDeleteBlocked(),
    isMapFull: () => Store.isMapFull(),
    findNode: (id) => Store.find(id),
    findParent: (id) => Store.findParent(id),
    setFocus: (id) => Store.setFocus(id),
    setSelected: (id) => Store.setSelected(id),
    updateNode: (id, updates) => CRDTBridge.updateNode(id, updates),
    addNode: (parentId, node) => CRDTBridge.addNode(parentId, node),
    removeNode: (id) => CRDTBridge.removeNode(id),
    switchView,
    openModal,
    showShortcutsModal: showShortcutHelp,
    showToast,
    toggleNotesOverlay: () => NotesOverlay.toggle(),
    toggleNodeRefnote: (nodeId) => NotesOverlay.toggleNodeRefnote?.(nodeId),
    captureRoutineNode: RoutinesView.captureRoutineNode,
    toggleRoutinesPanel: RoutinesView.togglePanel,
    openTagPopup: (nodeId, mouseX, mouseY) => TagPopup.open(nodeId, mouseX, mouseY),
    getTagDefinitions: (nodeId) => CRDTBridge.getTagDefinitions(nodeId),
    getHighlightedTagIds: () => Store.get('highlightedTagIds'),
    getHiddenTagIds: () => Store.get('hiddenTagIds'),
    makeEditable,
    generateId,
    getNewColor,
    getViewModule: (viewName) => views[viewName]?.module,
    screenToWorld: (x, y, parentId) => BubbleView.screenToWorld(x, y, parentId),
    setBubblePosition,
    getCurrentDocId: () => Store.get("currentDocId"),
    beginBatch: CRDTBridge.beginBatch,
    endBatch: CRDTBridge.endBatch,
    copyNodes,
    cutNodes,
    pasteNodes,
  });

  // Setup import/export handlers
  ImportExportManager.setupImportExport({
    getState: () => Store.getState(),
    withAllGanttRows: (fn) => GanttView.withAllRowsDrawn(fn),
    get: (key) => Store.get(key),
    find: (id) => Store.find(id),
    getAll: () => Store.getAll(),
    getUsername: () => getCachedUsername(),
    initialize: (data, opts) => Store.initialize(data, opts),
    renderTree,
    switchView,
    saveToLocalStorage,
    syncFullTree: CRDTBridge?.syncFullTree,
    setMetadata: CRDTBridge?.setMetadata,
    getMetadata: CRDTBridge?.getMetadata,
    setDocNotes: CRDTBridge?.setDocNotes,
    getDocNotes: CRDTBridge?.getDocNotes,
    getExecutionItems: CRDTBridge?.getExecutionItems,
    setExecutionItems: CRDTBridge?.setExecutionItems,
    getRoutineEvents: CRDTBridge.getRoutineEvents,
    setRoutineEvents: CRDTBridge.setRoutineEvents,
    getTaskPostponementEvents: CRDTBridge.getTaskPostponementEvents,
    setTaskPostponementEvents: CRDTBridge.setTaskPostponementEvents,
    // Bridge-aware passthroughs so the export path can pull fresh
    // content from the Y.XmlFragment when the doc has been migrated to
    // collaborative doc-notes (the array's `content` string is only
    // refreshed on editor blur and may lag).
    isCollabDocNotesEnabled: CRDTBridge?.isCollabDocNotesEnabled,
    serializeDocNote: CRDTBridge?.serializeDocNote,
    serializeDocNoteHtml: CRDTBridge?.serializeDocNoteHtml,
    getTagDefinitions: CRDTBridge?.getTagDefinitions,
    setTagDefinitions: CRDTBridge?.setTagDefinitions,
    addNode: (parentId, nodeData) => CRDTBridge.addNode(parentId, nodeData),
    updateNode: (id, data) => CRDTBridge.updateNode(id, data),
    beginBatch: () => CRDTBridge.beginBatch(),
    endBatch: () => CRDTBridge.endBatch(),
    // New callbacks for create-new-map flow
    createMap: VaultStorage
      ? (name, folderId, initialState) => VaultStorage.createMap(name, folderId, initialState, { whenOffline: "device" })
      : null,
    openDoc: DocumentLoader
      ? (docId) => DocumentLoader.handleDocOpen(docId)
      : null,
    getCurrentFolderId: () => null,
    getDocId: () => CRDTBridge.getActiveDocId(),
    getDek: () => CRDTBridge.getActiveEncryptionKey() || getAttachmentDek(CRDTBridge.getActiveDocId()),
    replaceTree: (tree) => CRDTBridge.replaceTree(tree),
    onUpdateNode: (id, updates) => CRDTBridge.updateNode(id, updates),
    openImportBrowser: async (exportData) => {
      const { openImportBrowser } = await import("./ui/import-browser.js");
      const selected = await openImportBrowser(exportData);
      if (!selected || selected.length === 0) return;

      // Build filtered export with only selected documents
      const filteredExport = {
        version: 2,
        exportedAt: exportData.exportedAt,
        folders: exportData.folders || [],
        documents: selected,
      };

      const result = await importAllData(filteredExport, { attachmentArchive: exportData._attachmentArchive });

      if (result.errors.length > 0) {
        alert(t("app.import.partial", { imported: result.imported, total: selected.length, errors: result.errors.join("\n") }));
      }

      // Open first imported document
      if (result.newDocIds && result.newDocIds.length > 0) {
        await DocumentLoader.handleDocOpen(result.newDocIds[0]);
      }
    },
  });

  // Setup theme selector
  getThemeApi()?.loadColorTheme?.();
  setupThemeSelector();
  document.getElementById("btn-new")?.addEventListener("click", () => {
    void VaultUI.promptNewDoc();
  });

  // Setup beforeunload handler to save positions before page close
  setupBeforeUnloadHandler();

  // Initialize toast notifications
  initToast();
  initQuotaNotice({
    getUser: () => AuthHandlers.getCurrentUser(),
    getDoc: id => VaultStorage.getDoc(id),
    getCurrentDocId: () => Store.get('currentDocId'),
    onChanged: () => SyncOrchestrator.updateSyncStatus(CRDTBridge.getConnectionState()),
    openVault: () => isMobileShellVisible() ? MobileApp.openVault() : VaultUI.open(),
  });
  initMapSizeNotice({
    getCurrentDocId: () => Store.get('currentDocId'),
    isMapFull: () => Store.isMapFull(),
    showToast,
  });
  initSyncPausedNotice({
    getTitle: id => VaultStorage.getDoc(id)?.title,
    onRetry: async ({ scope, id }) => {
      if (scope === 'vault') return UserCRDTBridge.retryPausedSync();
      // Open by its link: reconnecting follows a key change through the link.
      if (engine.sharing.link.isLinkMap(id)) {
        if (!await CRDTBridge.retryPausedMapSync(id)) await DocumentLoader.handleDocOpen(id);
        return;
      }
      // A retry must fetch and verify the declared key, even if old ciphertext
      // still decrypts. Network failures cannot count as verification success.
      const response = await fetch(`/api/shares/${id}/my-dek`, { credentials: 'include' });
      if (!response.ok) throw new Error('Map key is unavailable');
      const candidate = await response.json();
      if (candidate.is_revoked || !candidate.wrapped_dek) throw new Error('Map key is unavailable');
      const { verifyReplacement } = await import('../../engine/crypto/map-key-trust.js');
      await verifyReplacement(id, candidate.wrapped_dek);
      await VaultStorage.updateDocWrappedDEK(id, candidate.wrapped_dek);
      if (!await CRDTBridge.retryPausedMapSync(id)) await DocumentLoader.handleDocOpen(id);
    },
    onExport: async ({ scope, id }) => {
      const { chooseExportOptions } = await import('./ui/export-options.js');
      const options = await chooseExportOptions();
      if (!options) return;
      const backup = await import('../../engine/crypto/data-export.js');
      const flow = await import('./ui/export-flow.js');
      const { exportData, exportErrors } = await backup.exportAllData({ ...options, docIds: scope === 'map' ? [id] : undefined });
      await flow.downloadExport(exportData, null, options);
      if (!options.includeAttachments && exportErrors.length) flow.showExportWarnings(exportErrors);
    },
    // S-H2: the owner or an admin can move stored updates nobody can decrypt
    // out of sync, so one bad update no longer pauses the map for everyone.
    canSetAside: ({ id }) => {
      const role = VaultStorage.getDoc(id)?.role || 'owner';
      return (role === 'owner' || role === 'admin') && CRDTBridge.getUnreadableUpdates(id).length > 0;
    },
    onSetAside: async ({ id }) => {
      const count = CRDTBridge.getUnreadableUpdates(id).length;
      const ok = await confirmModal({
        title: t('app.setAside.title'),
        message: t('app.setAside.message', { count }),
        confirmText: t('app.setAside.confirm'),
      });
      if (!ok) return;
      const moved = await CRDTBridge.quarantineUnreadableUpdates(id);
      showToast(t('app.setAside.done', { count: moved }), { type: 'success' });
    },
  });

  // Initialize context menu
  ContextMenuManager.initContextMenu({
    contextMenuInit: () => ContextMenu.init(),
    contextMenuRegisterItems: (items) => ContextMenu.registerItems(items),
    getCurrentDocId: () => CRDTBridge.getCurrentDocId(),
    getDocIdForNode: (id) => CRDTBridge.getDocIdForNode(id),
    isReadOnly: () => Store.isReadOnly(),
    isNodeReadOnly: (nodeId) => CRDTBridge.isNodeReadOnly(nodeId),
    isDeleteBlocked: () => Store.isDeleteBlocked(),
    isNodeDeleteBlocked: (nodeId) => CRDTBridge.isNodeDeleteBlocked(nodeId),
    getState: () => Store.getState(),
    get: (key) => Store.get(key),
    findNode: (id) => Store.find(id),
    findParent: (id) => Store.findParent(id),
    addNode: (parentId, node) => CRDTBridge.addNode(parentId, node),
    removeNode: (id) => CRDTBridge.removeNode(id),
    setFocus: (id) => Store.setFocus(id),
    openModal,
    showRelatedNotes,
    addNodeToDailyList: NotesOverlay.addNodeToDailyList,
    switchView,
    showToast,
    getCurrentUser,
    copyNodes,
    cutNodes,
    pasteNodes,
    linkDocument,
    unlinkDocument,
    openDoc: (docId) => DocumentLoader?.handleDocOpen(docId),
    regenerateNodeIds,
    cleanNode,
  });

  // Global delegated dblclick → open doc for nested reference / duplicate
  // stub badges. Works across all views that render nodes with `data-id`.
  // Respects the user's spec: left-click / drag stay as-is in each view;
  // only a dblclick escalates to "open the linked doc as main".
  document.addEventListener("dblclick", (e) => {
    const el = e.target.closest?.("[data-id]");
    if (!el) return;
    const node = Store.find(el.dataset.id);
    if (!node?.linkedDocId) return;
    if (!(node._nestedReference || node._duplicate || node._idConflict)) return;
    e.preventDefault();
    e.stopPropagation();
    DocumentLoader?.handleDocOpen(node.linkedDocId);
  }, { capture: true });

  // Click a link's badge → open the linked map on its own, as its tooltip
  // says (linked, nested, or shown elsewhere: conflict). Not while loading
  // or offline, nor when there is no access (its own handler below) or
  // nothing left to open.
  document.addEventListener("click", (e) => {
    const badge = e.target.closest?.(".linked-badge");
    const wrapper = badge?.closest("[data-id]");
    const node = wrapper ? Store.find(wrapper.dataset.id) : null;
    if (!canOpenLinkedMap(node)) return;
    e.preventDefault();
    e.stopPropagation();
    DocumentLoader?.handleDocOpen(node.linkedDocId);
  }, { capture: true });

  // Click the 🔒 badge on a no-access stub → confirm modal → POST a
  // request-access for that doc. The badge stops propagation so clicking
  // it doesn't also select the node. Desktop views only (their badges sit
  // in [data-id]): the mobile app handles its own badges, and stopping its
  // click here left the badge doing nothing.
  document.addEventListener("click", async (e) => {
    const wrapper = e.target.closest?.(".linked-badge--no-access")?.closest("[data-id]");
    if (!wrapper) return;
    e.preventDefault();
    e.stopPropagation();
    await requestLinkedMapAccess(Store.find(wrapper.dataset.id));
  }, { capture: true });

  // Initialize document properties modal
  DocPropertiesModal.init({
    getCurrentDocId: () => Store.get("currentDocId") || "default",
    getDocTitle: (docId) => {
      if (!VaultStorage) return Store.getState()?.data?.name || t("common.untitled");
      const index = VaultStorage.getIndex();
      if (index) {
        const doc = (index.docs || []).find((m) => m.docId === docId);
        if (doc) return doc.title;
      }
      return Store.getState()?.data?.name || t("common.untitled");
    },
    renameDoc: async (docId, newTitle) => {
      if (VaultStorage) {
        await VaultStorage.renameMap(docId, newTitle);
      }
      // Also update the root node name in the CRDT
      const rootId = Store.getState()?.data?.id;
      if (rootId) {
        CRDTBridge.updateNode(rootId, { name: newTitle });
      }
    },
    getServerStatus: () => SyncOrchestrator.getServerStatus(),
    updateHeaderTitle: () => VaultUI.updateCurrentDocName(),
    isLoggedIn: () => !!getCurrentUser(),
  });

  // Doc title button opens properties modal
  const docTitleBtn = document.getElementById("doc-title-btn");
  if (docTitleBtn) {
    docTitleBtn.addEventListener("click", () => {
      DocPropertiesModal.open();
    });
  }

  // Where the open map is stored, next to its title; moving it online.
  MapStorage.initMapStorage({
    getCurrentUser: () => getCurrentUser(),
    reopenDoc: (docId) => VaultUI.openPersonalDoc(docId),
    openProperties: () => DocPropertiesModal.open(),
    openLogin: () => document.getElementById("header-login-btn")?.click(),
    // Another map than these opens (a map no longer online is about to be
    // removed from this device); none left: nothing stays open.
    closeMap: async (docIds) => {
      const next = VaultStorage.getIndex()?.docs?.find((doc) => !docIds.includes(doc.docId));
      if (next) {
        await VaultUI.openPersonalDoc(next.docId);
      } else {
        await closeMap();
        VaultStorage.setCurrentDocId(null);
      }
    },
  });

  // Online maps' attachments copied for offline use; files attached offline
  // sent when the connection returns.
  OfflineFiles.initOfflineFiles({
    getCurrentUser: () => getCurrentUser(),
    // The map whose sync just completed, and its content: from its document,
    // ready as soon as sync completes (the page may still be switching to it).
    getDocId: () => CRDTBridge.getCurrentDocId?.() || Store.get("currentDocId"),
    getTree: () => CRDTBridge.getTreeFromCRDT?.() || Store.getState()?.data,
    getDocNotes: () => CRDTBridge.getDocNotes?.() || [],
    getRoutineEvents: () => CRDTBridge.getRoutineEvents?.() || [],
  });

  initShortcutHelp({ show: showModalGeneric, close: closeModalGeneric });

  // Listen for blocked edit attempts (viewer trying to edit)
  on(Events.EDIT_BLOCKED, ({ action, reason }) => {
    showToast(reason, { type: "warning", duration: 2500 });
  });

  // This device's settings storage is full (shared/local-doc-state.js).
  on(Events.STORAGE_WARNING, ({ type } = {}) => {
    if (type === "quota_exceeded") showToast(t("app.prefsFull"), { type: "warning", duration: 8000 });
  });

  // Open the share-manage modal for a doc that received an access request
  // and pre-fill the invite input with the requester's username. The owner
  // picks a role and clicks Invite — server-side invite_user detects the
  // pending request and auto-accepts for the requester (see invitations.rs).
  on(Events.OPEN_SHARE_MODAL_FOR_REQUEST, async ({ docId, requesterUsername }) => {
    try {
      const { showManageShareModal } = await import("./ui/share-modal.js");
      // Fire-and-forget — the modal renders, we prefill after a tick.
      showManageShareModal(docId, docId).catch(() => {});
      // Poll briefly for the input to appear (the modal is async to build).
      const deadline = Date.now() + 2000;
      const fill = () => {
        const input = document.getElementById("invite-username");
        if (input) {
          input.value = requesterUsername;
          input.focus();
          input.dispatchEvent(new Event("input", { bubbles: true }));
          return;
        }
        if (Date.now() < deadline) requestAnimationFrame(fill);
      };
      requestAnimationFrame(fill);
    } catch (err) {
      console.error("[Main] Failed to open share modal for request:", err);
      showToast(t("app.share.openFailed"), { type: "error" });
    }
  });

  // When the user gains access to a doc (vault index updated with a
  // wrappedDEK for a docId that was previously pending), drop it from
  // the access-requests pending set so the stub loses its "request
  // pending" badge and starts syncing.
  on(Events.VAULT_INDEX_UPDATED, () => {
    const index = VaultStorage?.getIndex?.();
    if (!index?.docs) return;
    // Resolved once the map's key in the vault changed since the request
    // (a grant brings a new one; an unrelated vault save does not).
    AccessRequests.resolveGranted(index.docs);
  });

  // M-4: Warn user when offline queue overflows and edits may be lost
  window.addEventListener("orbital:offline-queue-overflow", (e) => {
    const count = e.detail?.dropped || 0;
    showToast(
      t("app.offlineQueue.dropped", { count }),
      { type: "warning", duration: 8000 },
    );
  });

  // Listen for batch end to trigger deferred render
  on(Events.DATA_CHANGED, ({ source }) => {
    if (source === "batch" && isClassicShown()) {
      renderTree();
      const currentView = Store.get("currentView");
      // Mobile: skip wholesale. The shell refreshes its own embedded view
      // after batch ops (refreshMountedDesktopView) and rebuilds visible
      // content through its own subscriber — running this too caused a
      // double full re-render of the embedded map on every fold.
      if (isMobileShellVisible()) return;
      const viewConfig = views[currentView];
      if (viewConfig?.module?.update) {
        const state = Store.getState();
        // Pass an empty object as "previous" so views' diff-based change
        // detection treats this as a wholesale refresh. Calling update(state,
        // state) made bubble see prev.data === next.data and skip the
        // structural-change branch entirely — paste landed in the CRDT but
        // never repainted. {} (vs undefined) is intentional: gantt does a
        // bare prevState.ganttZoom read that would TypeError on undefined.
        viewConfig.module.update({}, state);
      }
    }
  });

  // Register Store subscriber for re-rendering on state changes
  // This is critical for both online and offline modes
  // Phase 1.3 (PLAN17): Added options parameter for batch-awareness
  // Uses requestAnimationFrame to batch all DOM updates into a single paint frame,
  // preventing visual flickering from intermediate states.
  let _renderPending = false;
  Store.subscribe((state, prevState, action, options = {}) => {
    // Another interface is shown: classic draws nothing.
    if (!isClassicShown()) return;
    // Skip during document transitions - switchView() will do a full render after loading
    if (DocumentManager.isDocumentLoading()) {
      return;
    }

    // Skip during CRDT batching - will render once after batch ends
    if (CRDTBridge.isBatching()) {
      return;
    }

    // Phase 1.3 (PLAN17): Skip if this is an explicit batch update
    if (options.isBatchUpdate) {
      return;
    }

    // Phase 1.3 (PLAN17): Skip if only dimension fields changed (layout-only update)
    if (options.dimensionOnly) {
      return;
    }

    // Phase 3.1 Fix: Skip if this came from batch completion
    // The DATA_CHANGED listener handles batch renders to avoid double rendering
    if (action && action.includes('batch')) {
      return;
    }

    // Batch all DOM updates into a single animation frame to prevent flickering
    if (_renderPending) return;
    _renderPending = true;

    requestAnimationFrame(() => {
      _renderPending = false;

      // Get latest state (may have changed since subscriber fired)
      const latestState = Store.getState();

      // Re-render tree
      renderTree();

      // While the mobile shell is on screen the desktop chrome (#app, sidebar,
      // notes overlay) is display:none — updating it is invisible O(doc) work
      // on every edit. The one exception is a desktop view the mobile shell
      // has embedded (map tabs): that one is live and must keep updating.
      const mobileShellVisible = isMobileShellVisible();

      // Update active view (use .update() to avoid full re-render on minor changes).
      // Two views can carry .active at once on mobile: the hidden desktop one
      // (switchView) and the one embedded in the mobile shell (map tabs) —
      // prefer the embedded one ONLY while the shell is on screen; after a
      // flip to desktop the still-mounted preview must not shadow the
      // desktop's own active view.
      const activeView =
        (mobileShellVisible &&
          document.querySelector(".view.active.mobile-embedded-view")) ||
        document.querySelector(".view.active");
      if (activeView) {
        const isEmbedded = activeView.classList.contains("mobile-embedded-view");
        if (!mobileShellVisible || isEmbedded) {
          const viewId = activeView.id.replace("-view", "");
          const viewConfig = views[viewId];
          if (viewConfig?.module?.update) {
            viewConfig.module.update(prevState, latestState);
          }
        }
      }

      if (!mobileShellVisible) {
        // Update notes overlay if it exists
        if (NotesOverlay.update) {
          NotesOverlay.update(latestState);
        }

        // Update sidebar tags
        SidebarTags.render();
      }

      // Save to localStorage (for offline) or trigger autosave (for online)
      // — debounced; flushed on visibilitychange/beforeunload.
      scheduleLocalStateSave();
    });
  });

  // When the viewport crosses back to desktop (rotate, resize, external
  // display), the desktop view and sidebar skipped every render while the
  // mobile shell was visible — rebuild them once so the user doesn't see a
  // stale or empty view until their next edit.
  window.matchMedia(MOBILE_SHELL_MEDIA_QUERY).addEventListener("change", (e) => {
    if (e.matches || !Store.get("data")) return;
    // Reclaim the desktop view element from the now-hidden mobile shell first
    // (the shell only re-renders on its own events, so it wouldn't release it
    // on its own), then rebuild the desktop view and sidebar.
    MobileApp.releaseEmbeddedDesktopView?.();
    switchView(Store.get("currentView"));
  });

  _coreInitialized = true;
  console.log("[Orbital] Core initialization complete");
}

/**
 * Setup beforeunload handler
 *
 * NOTE: With CRDT architecture, saves happen in real-time via WebSocket.
 * The SyncManager handles compaction on visibility change (tab hidden).
 * We no longer save to legacy records here - that would be inconsistent
 * with the CRDT-first architecture.
 */
function setupBeforeUnloadHandler() {
  window.addEventListener("orbital:close-requested", flushLocalStateSave);
  window.addEventListener("beforeunload", (e) => {
    // Flush any pending debounced local-state save (per-device UI state)
    flushLocalStateSave();
    // Log if there are pending changes that might not have synced
    if (hasPendingChanges() && getCurrentUser()) {
      console.log("[BeforeUnload] Page unloading with pending changes - CRDT should have synced");
      // CRDT handles syncing automatically, no legacy save needed
    }
  });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      flushLocalStateSave();
    }
  });
}

// ============================================
// Theme Selector
// ============================================

const THEME_KEY = "project-planner-theme";

function setupThemeSelector() {
  const themeBtn = document.getElementById("btn-theme");
  const themeDropdown = document.getElementById("theme-dropdown");

  if (!themeBtn || !themeDropdown) return;

  // Load saved theme
  const savedTheme = getPref(THEME_KEY) || "dark";
  document.documentElement.setAttribute("data-theme", savedTheme);
  themeDropdown.querySelectorAll(".theme-option").forEach((opt) => {
    opt.classList.toggle("active", opt.dataset.theme === savedTheme);
  });

  themeBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    themeDropdown.classList.toggle("active");
    // Close export dropdown if open
    document.getElementById("export-dropdown")?.classList.remove("active");
  });

  themeDropdown.querySelectorAll(".theme-option").forEach((opt) => {
    opt.addEventListener("click", () => {
      const theme = opt.dataset.theme;
      document.documentElement.setAttribute("data-theme", theme);
      setPref(THEME_KEY, theme);
      themeDropdown.querySelectorAll(".theme-option").forEach((o) => {
        o.classList.toggle("active", o.dataset.theme === theme);
      });
      themeDropdown.classList.remove("active");
    });
  });

  // Close dropdown when clicking outside
  document.addEventListener("click", (e) => {
    if (!e.target.closest(".theme-selector")) {
      themeDropdown.classList.remove("active");
    }
  });
}

// Offline mode functions delegated to OfflineMode manager
async function initializeLoggedOutMode(options) {
  return OfflineMode.initializeLoggedOutMode(options);
}

async function handleLoggedOutDocOpen(docId) {
  return OfflineMode.handleLoggedOutDocOpen(docId);
}

async function handleLoggedOutDocCreate(docId) {
  return OfflineMode.handleLoggedOutDocCreate(docId);
}

async function handleLoggedOutDocDelete(docId) {
  return OfflineMode.handleLoggedOutDocDelete(docId);
}

// Mobile shell stashes unsaved note/node edits to localStorage when a
// doc-switch races a save. After a doc is permanently deleted, no recovery
// is possible — drop those stash entries so the user doesn't see a "Recover
// unsaved note?" prompt for content that can never be restored.
function dropMobileStashEntriesForDoc(docId) {
  if (!docId) return;
  // Delegate to the mobile module so it can scrub BOTH localStorage AND
  // its private in-memory stash mirrors (logged-in users keep stashes in
  // memory only — a localStorage-only scrub here would leak ghost
  // recoveries on the next edit).
  try {
    MobileApp.dropStashEntriesForDoc?.(docId);
  } catch (err) {
    console.warn("[Main] dropStashEntriesForDoc failed:", err);
  }
}

async function clearActiveDocumentAfterDelete() {
  try {
    await closeMap();
  } catch (err) {
    console.warn("[Main] Failed to stop CRDT after deleting active doc:", err);
  }
  VaultStorage?.setCurrentDocId?.(null);
  Store.reset();
  clearLastOpenedDoc();
  markSaved();
  try {
    emit(Events.VAULT_INDEX_UPDATED, { source: "active-doc-delete" });
  } catch (err) {
    // Best effort: Store.reset() is the important state transition.
  }
}


// ============================================
// Modal Wrapper (delegates to ModalManager)
// ============================================

/**
 * Open the node properties modal.
 * Wrapper that delegates to ModalManager for backward compatibility.
 */
function openModal(nodeId = null, options = {}) {
  ModalManager.openModal(nodeId, options);
}

// ============================================
// Auth & Screen Management
// ============================================

// ============================================
// Delta Sync - Change Tracking (delegates to DocumentManager)
// ============================================

// cleanNodeForSync moved to SyncOrchestrator

// Wrapper functions that delegate to DocumentManager
// (kept for backward compatibility with functions passed to AuthManager.init)
function markSaved() {
  DocumentManager.markSaved();
}

function clearDeltaSyncState() {
  DocumentManager.clearDeltaSyncState();
}

// Accessor for pendingChanges used in beforeunload handler
function hasPendingChanges() {
  return DocumentManager.hasPendingChanges();
}

// CRDT Sync Functions moved to SyncOrchestrator

// showScreen, hideAuthError, updateHeaderUI, api moved to auth-manager.js

// Auth functions moved to AuthManager
// - onAuthSuccess
// - onLogout
// - resetToDefaultMap
// - setupAuthHandlers


// ============================================
// Application Startup
// ============================================

// ============================================
// Tauri config UX (Phase 4 + 5b)
// ============================================
// Wires a single click affordance on the save indicator that lets
// the user change the vault path AND the server URL. The same
// helper runs for both local-only and online Tauri sessions so
// users can switch between modes without editing config.json.
//
// No-op on the web (getVaultPath is undefined when the web adapter
// is selected).
async function setupTauriConfigUX() {
  if (typeof getVaultPath !== "function") return;

  let vaultPath = "";
  let serverUrl = "";

  try {
    vaultPath = await getVaultPath();
    document.body.dataset.vaultPath = vaultPath;
    console.log("[Orbital] Vault path:", vaultPath);
  } catch (err) {
    console.warn("[Orbital] Could not read vault path:", err);
    return;
  }

  if (typeof getServerOrigin === "function") {
    serverUrl = getServerOrigin() || "";
    if (serverUrl) document.body.dataset.serverUrl = serverUrl;
  }

  // Update the save-indicator text + tooltip. In online mode the
  // sync-orchestrator owns the text via state changes, so don't
  // overwrite — only update tooltip.
  const saveText = document.getElementById("save-text");
  if (saveText) {
    if (!isSyncAvailable()) {
      saveText.textContent = t("app.status.savedToFile");
    }
    const tooltip = serverUrl
      ? t("app.tauri.tooltipServer", { vault: vaultPath, server: serverUrl })
      : t("app.tauri.tooltipLocal", { vault: vaultPath });
    saveText.title = tooltip;
    saveText.style.cursor = "pointer";

    // Sequential prompts: vault path, then server URL. Empty
    // server URL = clear (return to local-only). Reload only if
    // anything actually changed.
    saveText.addEventListener("click", async (e) => {
      // In online mode the save indicator click might compete with
      // other handlers (e.g. the login-to-sync button). Only react
      // to direct clicks on the text element to avoid that.
      if (e.target !== saveText) return;

      const newVaultPath = window.prompt(
        t("app.tauri.promptVault"),
        vaultPath,
      );
      if (newVaultPath === null) return; // user cancelled

      const newServerUrl = window.prompt(
        t("app.tauri.promptServer"),
        serverUrl,
      );
      if (newServerUrl === null) return; // user cancelled

      const trimmedVault = newVaultPath.trim();
      const trimmedServer = newServerUrl.trim();
      const vaultChanged = trimmedVault && trimmedVault !== vaultPath;
      const serverChanged = trimmedServer !== serverUrl;

      if (!vaultChanged && !serverChanged) return;

      try {
        await reloadApp(async () => {
          if (serverChanged && typeof setServerUrl === "function") {
            await setServerUrl(trimmedServer || null);
          }
          if (vaultChanged) {
            await setVaultPath(trimmedVault);
          }
        });
      } catch (err) {
        window.alert(t("app.tauri.saveFailed", { error: String(err) }));
      }
    });
  }
}

async function startApp() {
  // Take over the static splash from index.html. Reaching this line means the
  // bundle is downloaded and parsed — on slow devices the longest boot phase.
  LoadProgress.adoptBootSplash();
  LoadProgress.stage(0.3, "Starting…");

  // Language first: everything shown from here on uses it (shared/i18n/index.js).
  await initI18n();
  mountLanguageButtons({
    // Only when signed in; signed out, the choice stays on this device.
    saveToAccount: (code) => AuthHandlers.getCurrentUser()
      ? AuthHandlers.api("/api/account/preferences", "PUT", { locale: code })
      : null,
    reload: () => reloadApp(),
  });

  initThemes();

  // Which interface this page shows (classic, or another: interfaces/index.js).
  await Interfaces.chooseAtStart({ showClassic: () => DocumentLoader.showOpenMap() });

  initStorageBrokenModal({
    forceCompaction: (reason) => CRDTBridge.forceCompaction(reason),
    logout: () => AuthHandlers.logout(),
  });

  // Setup auth handlers (delegates to AuthManager)
  AuthManager.setupAuthHandlers();

  // Setup profile handlers (delegates to ProfileManager)
  ProfileManager.setupProfileHandlers();

  // Login to sync button (appears when session expired)
  const loginToSyncBtn = document.getElementById("login-to-sync-btn");
  const continueOfflineContainer = document.getElementById("continue-offline-container");
  const continueOfflineBtn = document.getElementById("continue-offline-btn");

  if (loginToSyncBtn) {
    loginToSyncBtn.addEventListener("click", async () => {
      // Check server health before showing login
      loginToSyncBtn.disabled = true;
      loginToSyncBtn.textContent = t("app.header.checking");

      const serverOnline = await SyncOrchestrator.checkServerHealth();

      loginToSyncBtn.disabled = false;
      loginToSyncBtn.textContent = t("html.header.loginToSync");

      if (serverOnline && AuthHandlers.getCachedUserId() && AuthHandlers.getCachedUsername()) {
        // This device's account: its password only, over the work in progress
        // (interfaces/classic/ui/session-resume.js); the page reloads once signed in, to sync.
        void openSessionResume({ reloadAfter: true });
      } else if (serverOnline) {
        // Show "continue offline" option since user was working offline
        if (continueOfflineContainer) {
          continueOfflineContainer.classList.remove("hidden");
        }
        showScreen("login-screen");
      } else {
        SyncOrchestrator.showConnectionErrorModal();
      }
    });
  }

  // Continue offline button - go back to main app without logging in
  if (continueOfflineBtn) {
    continueOfflineBtn.addEventListener("click", (e) => {
      e.preventDefault();
      // Hide the continue offline option for next time
      if (continueOfflineContainer) {
        continueOfflineContainer.classList.add("hidden");
      }
      showScreen("main-app");
    });
  }

  // Setup browser network listeners for offline/online detection
  SyncOrchestrator.setupNetworkListeners();

  // Tauri-only: dismiss authentication without forgetting the server.
  // Keep the current workspace, or open the device-local vault when startup
  // required authentication before any document could be loaded.
  if (typeof setServerUrl === "function") {
    document.querySelectorAll(".auth-screen .auth-box").forEach((box) => {
      if (box.querySelector(".auth-close-btn")) return;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "auth-close-btn";
      btn.setAttribute("aria-label", t("app.authClose.aria"));
      btn.title = t("app.authClose.title");
      btn.textContent = "×";
      btn.addEventListener("click", async () => {
        // Let an in-flight login/registration finish before changing views.
        if (btn.disabled || box.querySelector('button[type="submit"]:disabled')) return;
        btn.disabled = true;
        box.inert = true;
        try {
          if (!Store.get("data")) await AuthManager.resetToDefaultMap();
          // The old reload cleared these secrets; dismissal must do so too.
          document.querySelectorAll('.auth-screen input[type="password"], #recover-mnemonic')
            .forEach(input => { input.value = ""; });
          showScreen("main-app");
        } catch (err) {
          console.error("[Orbital] Could not return to workspace:", err);
          window.alert(t("app.authClose.failed"));
        } finally {
          box.inert = false;
          btn.disabled = false;
        }
      });
      box.insertBefore(btn, box.firstChild);
    });
  }

  // Phase 5a: load any persisted Tauri adapter config (server URL,
  // future settings) before the bootstrap routes between local-only
  // and online modes. On the web, this method is undefined — feature
  // detect, don't await a missing function.
  if (typeof loadAdapterConfig === "function") {
    await loadAdapterConfig();
  }
  // Phase 5b: install the fetch proxy IMMEDIATELY after config load
  // and BEFORE any code path that might fetch — so every `/api/*`,
  // `/auth/*`, etc. relative URL across the codebase routes to the
  // configured server origin instead of the tauri:// page origin.
  // No-op when the server URL isn't set (local-only mode).
  if (typeof installFetchProxy === "function") {
    installFetchProxy();
  }

  // Phase 7 prep: install the global CSRF token injector. Wraps
  // window.fetch so every state-changing request (POST/PUT/DELETE/
  // PATCH) carries the X-CSRF-Token header echoed from the
  // csrf_token cookie. The api() helper sets it explicitly; this
  // wrapper catches every other direct-fetch call site (~40 across
  // share-modal, notifications, attachment-service, etc.). Web and
  // Tauri both. Idempotent.
  AuthHandlers.installCsrfInjector?.();

  // Phase 7 prep: tighten the CSP connect-src for XSS-exfil defence.
  //
  // The static CSP in tauri.conf.json keeps connect-src wide enough
  // (`http: https: ws: wss:`) to reach any user-configured server
  // host, because the static config can't know it at build time.
  // Now that we've loaded the runtime config and know the actual
  // origin, we inject a meta CSP that intersects with the static
  // one — narrowing connect-src to JUST the configured server
  // (plus `'self'`, i.e. tauri://localhost for Tauri internals).
  //
  // If no server is configured (Tauri local-only mode), connect-src
  // shrinks to just `'self'` — no remote fetch is possible at all.
  //
  // Tauri's own IPC address (`ipc:` on Linux and macOS,
  // http://ipc.localhost on Windows) must stay allowed: without it every
  // command falls back to postMessage, which carries JSON only, so the
  // commands that take raw bytes (attachment files) fail.
  //
  // Per-CSP-spec, multiple policies intersect: an injected meta tag
  // can only further restrict, never broaden. So the static policy
  // is the upper bound; this meta is the operational policy.
  //
  // No effect on the web build: `getServerOrigin` is undefined
  // there, the typeof check skips the injection, and the page-level
  // CSP from the deployment continues to apply unchanged.
  if (typeof getServerOrigin === "function") {
    const serverOrigin = getServerOrigin();
    const connectSrc = ["'self'", "ipc:", "http://ipc.localhost"];
    if (serverOrigin) {
      const wsOrigin = serverOrigin
        .replace(/^http:/, "ws:")
        .replace(/^https:/, "wss:");
      connectSrc.push(serverOrigin, wsOrigin);
    }
    const cspMeta = document.createElement("meta");
    cspMeta.setAttribute("http-equiv", "Content-Security-Policy");
    cspMeta.setAttribute("content", `connect-src ${connectSrc.join(" ")}`);
    document.head.appendChild(cspMeta);
    console.log(
      "[Orbital] CSP connect-src narrowed to:",
      connectSrc.join(" "),
    );
  }

  // Tauri runtime: install the click-to-configure UX before deciding
  // local-only vs online routing, so the affordance is available in
  // both modes (the Phase 5b "switch modes" path needs it from
  // online too — users can explicitly change or clear the server here).
  await setupTauriConfigUX();

  // Tauri local-only mode: skip every network-dependent bootstrap
  // branch and jump straight to logged-out (= local-only) mode.
  // This avoids three wasted fetches (tryRestoreSession, /health,
  // /api/auth/me) that would all fail before falling through anyway,
  // and keeps the startup path identical to a fresh web user with no
  // cookie — same code path, just reached without the round trip.
  if (!isSyncAvailable()) {
    console.log("[Orbital] Local-only runtime detected, skipping network bootstrap");
    document.body.dataset.mode = "local-only";

    const localUser = getOrCreateLocalUser();
    const localKey = getOrCreateLocalMasterKey();
    await initializeLoggedOutMode({
      userId: localUser.user_id,
      masterKey: localKey,
      isLoggedOut: true,
    });
    showScreen("main-app");
    return;
  }

  // Try to restore session from stored master key (survives page refresh)
  LoadProgress.stage(0.5, t("app.boot.restoringSession"));
  const restoreResult = await AuthHandlers.tryRestoreSession();
  if (restoreResult.success) {
    console.log("[Orbital] Session restored from stored key");
    showScreen("main-app");
    return;
  }

  // Session revoked server-side (password change with "sign out other devices",
  // account recovery, deletion) while this device was restoring from its stored
  // key. tryRestoreSession has already discarded that key; falling through to
  // offline mode would hand back the access the revocation just removed.
  // Local documents are untouched — signing in with the new password reopens
  // them, including anything edited offline and not yet synced.
  if (restoreResult.revoked) {
    console.log("[Orbital] Session was revoked — password required");
    showScreen("login-screen");
    return;
  }

  // Session expired but we have stored key - start in offline CRDT mode
  if (restoreResult.sessionExpired && AuthHandlers.retrieveMasterKey()) {
    console.log("[Orbital] Session expired but have master key - using offline CRDT mode");
    const cachedUserId = getCachedUserId();
    const storedMasterKey = AuthHandlers.retrieveMasterKey();

    if (cachedUserId && storedMasterKey) {
      await initializeLoggedOutMode({
        userId: cachedUserId,
        masterKey: storedMasterKey,
        isLoggedOut: false  // This is offline recovery, not logged-out mode
      });
      showScreen("main-app");

      // Update header to show username (offline mode)
      const cachedUsername = getCachedUsername();
      if (cachedUsername) {
        updateHeaderUI({ username: cachedUsername, user_id: cachedUserId });
      }

      // Show "Login to sync" button directly (we know server is down)
      const loginToSyncBtn = document.getElementById("login-to-sync-btn");
      if (loginToSyncBtn) {
        loginToSyncBtn.classList.remove("hidden");
      }

      // Also update sync status indicator
      SyncOrchestrator.updateSyncStatus(ConnectionState.DISCONNECTED);

      // Its password, over the map it opened (interfaces/classic/ui/session-resume.js); the
      // page reloads once signed in, to start its sync. "Later" works on.
      void openSessionResume({ reloadAfter: true });

      // Try to restore session in background - schedule periodic retries.
      // Store interval ID so it can be cleared if user logs in manually.
      const retryInterval = setInterval(async () => {
        // If user already logged in via UI (which triggers reload), skip
        if (getCurrentUser()) {
          clearInterval(retryInterval);
          return;
        }
        console.log("[Orbital] Attempting background session restore...");
        const retry = await AuthHandlers.tryRestoreSession();
        if (retry.success) {
          console.log("[Orbital] Background session restore succeeded!");
          clearInterval(retryInterval);
          void reloadApp();
        }
      }, 30000);
    } else {
      // No cached user ID - fall back to fresh logged-out mode
      console.log("[Orbital] No cached user ID, starting fresh logged-out mode");
      const localUser = getOrCreateLocalUser();
      const localKey = getOrCreateLocalMasterKey();
      await initializeLoggedOutMode({
        userId: localUser.user_id,
        masterKey: localKey,
        isLoggedOut: true
      });
      showMainAppUnlessSigningIn();
    }
    return;
  }

  // Check for existing session
  LoadProgress.stage(0.6, t("app.boot.checkingSession"));
  const user = await AuthHandlers.checkSession();

  if (user) {
    // User has session but we need master key - show login to unlock vault
    console.log("[Orbital] Session found, but vault locked - please log in");
    showScreen("login-screen");
  } else {
    // No session at all - show logged-out mode
    console.log("[Orbital] No session - starting logged-out mode");
    const localUser = getOrCreateLocalUser();
    const localKey = getOrCreateLocalMasterKey();
    await initializeLoggedOutMode({
      userId: localUser.user_id,
      masterKey: localKey,
      isLoggedOut: true
    });
    showMainAppUnlessSigningIn();
  }
}

// Signed out, the header's Login button works while the local map still
// loads: keep a sign-in screen the user opened meanwhile instead of closing
// it under them when start-up finishes.
function showMainAppUnlessSigningIn() {
  const current = getCurrentScreen();
  if (current && current !== "main-app") return;
  showScreen("main-app");
  // A map's link waits (ui/link-join.js): it opens for this page, with a bar
  // offering to sign in and keep it.
  if (pendingLink()) {
    void openPendingLinkForPage({
      openMap: (docId) => handleLoggedOutDocOpen(docId),
      signIn: () => showScreen("login-screen"),
    });
  }
}

// Register the service worker: precached app shell = launches that don't
// wait on the network, plus offline capability. Web builds only — Tauri has
// no use for it, and dev servers don't generate sw.js. Registered after boot
// so the SW install never competes with the startup path for bandwidth/CPU.
function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  if (typeof window.__TAURI_INTERNALS__ !== "undefined") return;
  if (APP_VERSION === "dev") return;
  navigator.serviceWorker.register("/sw.js").catch((err) => {
    console.warn("[Orbital] Service worker registration failed:", err);
  });
}

// Start the app when DOM is ready
async function bootstrap() {
  try {
    // A map's link (`/join#…`): out of the address bar at once, kept for
    // this tab, opened once signed in (ui/link-join.js); a map whose link
    // stops working says so.
    captureLinkFromAddress();
    watchLinkGone({ getCurrentDocId: () => Store.get('currentDocId') });
    await startApp();
  } finally {
    // Single exit point for the boot splash: whichever branch startApp took
    // (main app, login screen, offline fallback), the boot phase is over.
    // No-op if a branch already closed it (e.g. onAuthSuccess).
    LoadProgress.done();
    registerServiceWorker();
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootstrap);
} else {
  bootstrap();
}

/** Explicit task action; selection alone never changes the note filter. */
async function showRelatedNotes(nodeId) {
  if (isMobileShellVisible()) return MobileApp.openRelatedNotes(nodeId);
  const task = createTaskFilter(nodeId, sharedCallbacks);
  if (!task) return;
  if (Store.get('currentView') === 'notes') setTaskFilter(task, true);
  else await NotesOverlay.showRelatedNotes(task);
}
